/**
 * BMS Navigation Tools — Client-side DOM manipulation for LangGraph chatbot.
 * ADDED BY: LangGraph chatbot integration (Ben Fung / ML Team)
 *
 * Uses the __TOOL_CALL__ / __TOOL_RESULT__ protocol (same as dummy_website2).
 * Plain JS functions — no browser-specific APIs required.
 */

import moment from 'moment';
import history from '../history';  // React Router's createBrowserHistory()

// ==================== Navigation guard: protect unsaved edits ====================
// BMS edit screens (ShippingOrder, Consolidation, Party, Rate, ...) render a
// react-router <Prompt when={editable} message="...">. history.js uses plain
// createBrowserHistory() with NO custom getUserConfirmation, so on history.push the
// Prompt falls back to a NATIVE window.confirm() — the "Are you sure you want to
// leave?" box (the browser shows it as "insight.mrhkg.com says Are you sure you
// want to leave?"). When the chatbot navigates AWAY from a page that is in Edit
// mode (unsaved changes), we now SURFACE that native confirm to the user instead of
// silently leaving and discarding their edits. On non-edit pages the Prompt's
// `when` is false, so no confirm fires and navigation stays silent as before.
//
// window.confirm blocks the JS event loop while shown — that is intentional here:
// we need a real user decision before the chatbot may leave an edited page. Because
// it blocks, the chatbot's antd popup handlers cannot run concurrently, but the user
// resolves this dialog first. antd Modal.confirm popups are NOT window.confirm and
// are unaffected.
let _chatbotNavigating = false;
let _navGuardMessage = null;   // the guard message that fired this push, if any
let _navGuardConfirmed = true; // did the user accept (Leave)? false == they stayed
const _origWindowConfirm = window.confirm.bind(window);
window.confirm = function (message) {
  if (_chatbotNavigating) {
    _navGuardMessage = message;
    // Show the REAL confirm so the user can keep their unsaved edits if they wish.
    _navGuardConfirmed = !!_origWindowConfirm(message);
    console.warn('[Chatbot] navigation guard (window.confirm):', message, '->', _navGuardConfirmed ? 'leave' : 'stay');
    return _navGuardConfirmed;
  }
  return _origWindowConfirm(message);
};
// history.push that respects BMS's "Are you sure you want to leave?" guard.
// Returns { confirmed, message }:
//   confirmed === true  -> navigation proceeded (no guard was active, or the user
//                          clicked Leave).
//   confirmed === false -> the user clicked Stay/Cancel; the route is UNCHANGED. The
//                          caller MUST abort any post-navigation steps and tell the
//                          agent the user stayed to keep unsaved edits
//                          (see _navCanceledResult).
// In-chatbox reminder shown when a navigation may trigger the browser-level
// "Are you sure you want to leave?" confirm. The native dialog is APP-modal (it
// floats over the whole page, not inside the chatbox), so a user watching the
// chatbox can miss it; this banner tells them to click it. Self-contained DOM
// (bmsTools is a DOM-automation module) — no React plumbing needed.
function _showNavGuardNotice() {
  let el = document.getElementById('chatbot-nav-guard-notice');
  if (!el) {
    el = document.createElement('div');
    el.id = 'chatbot-nav-guard-notice';
    el.setAttribute('role', 'status');
    el.textContent = 'This page may have unsaved changes. A "Are you sure you want to leave?" confirmation has opened in your browser — please click it (Leave to discard, or Cancel to stay on this page).';
    el.style.cssText = [
      'position:absolute', 'left:10px', 'right:10px', 'bottom:8px',
      'background:#fffbe6', 'border:1px solid #faad14', 'color:#614700',
      'padding:8px 10px', 'border-radius:6px', 'font-size:12px', 'line-height:1.35',
      'z-index:100000', 'box-shadow:0 2px 8px rgba(0,0,0,.15)',
      'display:none', 'pointer-events:none',
    ].join(';');
    const host = document.getElementById('chatWindow') || document.body;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.appendChild(el);
  }
  el.style.display = 'block';
}
function _hideNavGuardNotice() {
  const el = document.getElementById('chatbot-nav-guard-notice');
  if (el) el.style.display = 'none';
}

function safePush(url) {
  _navGuardMessage = null;
  _navGuardConfirmed = true;
  _chatbotNavigating = true;
  // Show the reminder BEFORE the push. Browsers paint pending DOM mutations
  // before a modal window.confirm, so this is visible while the "Are you sure
  // you want to leave?" dialog is up. On a non-edit page no confirm fires and
  // history.push returns within the same frame, so this never paints (no flicker).
  _showNavGuardNotice();
  try {
    history.push(url);
  } finally {
    _chatbotNavigating = false;
    _hideNavGuardNotice();
  }
  return { confirmed: _navGuardConfirmed, message: _navGuardMessage };
}

// Standard tool result when the user declines the "Are you sure you want to leave?"
// guard — i.e. they chose to stay and keep unsaved edits. Phrased for the agent so it
// stops the current navigation chain and asks the user to save/discard changes first.
function _navCanceledResult(_message) {
  return {
    content: [{
      type: "text",
      text: 'The user chose to stay on the current page to keep their unsaved edits, so navigation was canceled. Stop navigating and ask the user to save or discard their changes on this page before trying again.',
    }],
  };
}

// ==================== Route Map ====================

const ROUTE_MAP = {
  // Booking
  booking_dashboard:       "/BMS/enhance_booking",
  search_booking:          "/BMS/enhance_booking/search_booking",
  create_manual_booking:   "/BMS/enhance_booking/shipping_order/new",
  reuse_booking:           "/BMS/enhance_booking/reuse_booking",
  new_e_booking:           "/BMS/enhance_booking/new_e_booking",
  new_manual_booking:      "/BMS/enhance_booking/new_manual_booking",
  amend_e_booking:         "/BMS/enhance_booking/amend_e_booking",
  booking_pending_si:      "/BMS/enhance_booking/booking_pending_si",
  cancel_e_booking:        "/BMS/enhance_booking/cancel_e_booking",
  booking_confirmed:       "/BMS/enhance_booking/booking_confirmed",
  booking_cancelled:       "/BMS/enhance_booking/booking_cancelled",

  // Booking Approval
  ba_dashboard:            "/BMS/enhance_booking_approval",
  search_booking_approval: "/BMS/enhance_booking_approval/search_booking_approval",
  pending_for_approval:    "/BMS/enhance_booking_approval/pending_for_approval",
  request_approval:        "/BMS/enhance_booking_approval/request_approval",
  pending_customer_approval: "/BMS/enhance_booking_approval/pending_customer_approval",
  booking_approved:        "/BMS/enhance_booking_approval/booking_approved",
  booking_rejected:        "/BMS/enhance_booking_approval/booking_rejected",
  booking_advice:          "/BMS/enhance_booking_approval/booking_advice",
  ba_updated_by_mr:        "/BMS/enhance_booking_approval/booking_approval_updated_by_mr",
  ba_updated_by_customer:  "/BMS/enhance_booking_approval/booking_approval_updated_by_customer",

  // Consolidation
  consol_dashboard:        "/BMS/consolidation",
  search_consolidation:    "/BMS/consolidation/search_consolidation",
  create_consol:           "/BMS/consolidation/consolidation_management/new",
  pending_consol_creation: "/BMS/consolidation/pending_consolidation_creation",
  request_consol_approval: "/BMS/consolidation/request_consolidation_approval",
  pending_customer_consol: "/BMS/consolidation/pending_customer_consolidation_approval",
  consol_approved:         "/BMS/consolidation/consolidation_approved",
  consol_rejected:         "/BMS/consolidation/consolidation_rejected",
  consol_advice:           "/BMS/consolidation/consolidation_advice",
  amended_booking_pending: "/BMS/consolidation/amended_booking_pending",
  load_plan_pending:       "/BMS/consolidation/load_plan_pending_allocation",

  // Master BL
  master_bl_dashboard:     "/BMS/master_bl",
  search_master_bl:        "/BMS/master_bl/search_master_bl",
  create_master_bl:        "/BMS/master_bl/master_bl/new",

  // Sailing Schedule & Rate
  search_sailing_schedule:     "/BMS/sailingScheduleAndRate/search",
  sailing_schedule_maintenance: "/BMS/sailingScheduleAndRate/sailing_schedule_maintenance",
  rate_maintenance:            "/BMS/sailingScheduleAndRate/rate_maintenance",

  // Party
  party_dashboard:             "/BMS/party",
  search_party:                "/BMS/party/search_party",
  create_party:                "/BMS/party/party_maintenance/new",
  party_pending_approval:      "/BMS/party/pending_party_approval",
  party_pending_mgmt_approval: "/BMS/party/pending_party_management_approval",
  mgmt_approved:               "/BMS/party/party_management_approved",
  party_approved:              "/BMS/party/party_approved",
  party_sunac_pending:         "/BMS/party/pending_party_sunac_approval",
  party_sunac_handled:         "/BMS/party/party_sunac_approved",
  party_sunac_rejected:        "/BMS/party/party_sunac_rejected",
  party_cam_pending:           "/BMS/party/pending_for_party_sunac_cam_approval",
  party_cam_handled:           "/BMS/party/party_sunac_approved_cam",
  party_cam_rejected:          "/BMS/party/party_sunac_rejected_cam",
  party_rejected:              "/BMS/party/party_admin_rejected",
  mgmt_rejected:               "/BMS/party/party_management_rejected",
  search_customer_group_code:  "/BMS/party/account_group_code_maintenance",

  // Notification
  email_notification: "/BMS/NotificationCenter/search_email_notification/",
  messenger:          "/BMS/NotificationCenter/search_messenger",

  // SOPMS
  routing_template: "/SOPMS/routing_template/search_routing_template",

  // Analytics (22 Power BI pages)
  analytics_air_agent:           "/BMS/AirAgentReportPBI",
  analytics_air_shipment:        "/BMS/AirShipmentReportPBI",
  analytics_air_target:          "/BMS/AirTargetReportPBI",
  analytics_booking:             "/BMS/BookingPBI",
  analytics_carrier:             "/BMS/CarrierReportPBI",
  analytics_carrier_mqc:         "/BMS/CarrierMQC",
  analytics_carrier_performance: "/BMS/CarrierPerformance",
  analytics_carrier_perf_summary: "/BMS/CarrierPerformanceSummary",
  analytics_co2e:                "/BMS/CO2eEmissionReportPBI",
  analytics_insight_tracker:     "/BMS/InsightTrackerReportPBI",
  analytics_invoice_gp:          "/BMS/InvoiceGPPBI",
  analytics_manor:               "/BMS/ManorReportPBI",
  analytics_operation:           "/BMS/OperationReportPBI",
  analytics_operation_alert:     "/BMS/OperationAlertDashboard",
  analytics_productivity:        "/BMS/ProductivityReportPBI",
  analytics_profitability:       "/BMS/ProfitabilityReportPBI",
  analytics_sales_profitability: "/BMS/SalesProfitabilityReportPBI",
  analytics_profitability_target: "/BMS/ProfitabilityTargetReportPBI",
  analytics_sea_agent:           "/BMS/AgentReportPBI",
  analytics_sea_shipment:        "/BMS/ShipmentReportPBIDataImport",
  analytics_sea_target:          "/BMS/SeaTargetReportPBI",
  analytics_vendor:              "/BMS/VendorReportPBI",
  analytics_weblog:              "/BMS/WeblogTrackerReportPBI",

  // Admin
  seq_maintenance:       "/BMS/Admin/SEQ/SequenceSearch",
  party_group:           "/BMS/Admin/party_group_maintenance",
  tradelane:             "/BMS/Admin/tradelane_maintenance",
  user_maintenance:      "/BMS/Admin/user_maintenance",
  warehouse:             "/BMS/Admin/warehouse_maintenance",
  carrier:               "/BMS/Admin/carrier_maintenance",
  sys_notice:            "/BMS/Admin/sys_notice_maintenance",
  user_registration:     "/BMS/Admin/user_registration_maintenance",

  // EDI
  edi_recipients:    "/BMS/EDI/email_recipients/edisystem",
  oms_recipients:    "/BMS/EDI/email_recipients/oms",
  aldo_recipients:   "/BMS/EDI/email_recipients/omsaldo",
  steinel_recipients: "/BMS/EDI/email_recipients/omsse",
  schedule_email:    "/BMS/EDI/schedule_email",

  // Setting
  user_profile:     "/BMS/Setting/user_profile",
  change_password:  "/BMS/Setting/update_user_password",
  template_library: "/BMS/Setting/TMPLIB/TemplateSearch",
};


// ==================== DOM Helpers ====================

/**
 * Wait for an element matching the selector to appear in the DOM.
 * Uses MutationObserver — non-polling, React-friendly.
 * Returns the element or null if timeout.
 */
function waitForElement(selector, timeoutMs = 3000) {
  return new Promise((resolve) => {
    const el = document.querySelector(selector);
    if (el) return resolve(el);

    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        resolve(el);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    setTimeout(() => {
      observer.disconnect();
      resolve(document.querySelector(selector)); // last try
    }, timeoutMs);
  });
}

/**
 * Wait for React to finish rendering after an action.
 * Uses requestAnimationFrame for one tick — enough for React's sync renders.
 */
function waitForRender(timeoutMs = 1000) {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
    // Safety timeout
    setTimeout(resolve, timeoutMs);
  });
}


// ==================== Popup Detection & Handling ====================

/**
 * Robust visibility check for an element. Used instead of the antd
 * `ant-modal-wrap-hidden` class toggle, which is unreliable during open/close
 * transitions and in custom React roots (Modal.useModal / App.useApp). NEVER use
 * the jQuery-style `:visible` pseudo-selector with querySelectorAll — it throws.
 */
function isElementVisible(el) {
  if (!el) return false;
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  if (el.getAttribute('aria-hidden') === 'true') return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/**
 * Wait up to timeoutMs for a BLOCKING popup (modal/popconfirm) to appear.
 * BMS confirm dialogs (e.g. "Do you want to overwrite the following e-Booking
 * info by PRS info?") are produced by an async server round-trip and can mount
 * 1-3s AFTER the click. A single snapshot taken right after the click misses
 * them, so the LLM never learns a popup is blocking (M1). This polls + observes
 * DOM mutations until a blocking popup is present or the budget elapses.
 */
function waitForPopup(timeoutMs = 4000) {
  return new Promise((resolve) => {
    const hasBlocking = () => detectPopups().some(p => p.type === 'modal' || p.type === 'popconfirm');
    if (hasBlocking()) return resolve(true);
    const start = Date.now();
    let done = false;
    let poll;
    const finish = (val) => {
      if (done) return;
      done = true;
      observer.disconnect();
      clearInterval(poll);
      resolve(val);
    };
    const observer = new MutationObserver(() => { if (hasBlocking()) finish(true); });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'aria-hidden'] });
    poll = setInterval(() => {
      if (hasBlocking()) finish(true);
      else if (Date.now() - start >= timeoutMs) finish(false);
    }, 200);
  });
}

/**
 * Detect all visible popups on the page.
 * Returns array of { type, title, body, buttons } for each popup.
 * Types: 'modal', 'popconfirm', 'notification', 'toast'
 */
/**
 * Return the action-button container of an antd modal. Modal.confirm() (the
 * imperative API used for "Warning"/"Do you want to overwrite..." prompts)
 * renders its OK/Cancel buttons in .ant-modal-confirm-btns, whereas the <Modal>
 * component renders them in .ant-modal-footer. Tools must check both or
 * Modal.confirm popups look like they have no buttons and can never be clicked.
 */
function modalFooterContainer(modal) {
  return modal.querySelector('.ant-modal-confirm-btns') || modal.querySelector('.ant-modal-footer');
}

function detectPopups() {
  const popups = [];

  // 1. Ant Design Modal dialogs (Modal.confirm, Modal.warning, Modal.info, <Modal>)
  //    Drive visibility off the INNER .ant-modal (isElementVisible), NOT the
  //    .ant-modal-wrap-hidden class toggle, so modals mid-transition or in custom
  //    React roots are detected. Title/body read from multiple antd slot variants.
  for (const wrap of document.querySelectorAll('.ant-modal-wrap')) {
    const modal = wrap.querySelector('.ant-modal');
    if (!modal || !isElementVisible(modal)) continue;

    const title = (modal.querySelector('.ant-modal-title, .ant-modal-header-title, [class*="confirm-title"]')?.textContent || '').trim();
    const body = (modal.querySelector('.ant-modal-body, [class*="confirm-content"]')?.textContent || '').trim().substring(0, 300);
    const footer = modalFooterContainer(modal);
    const buttons = footer
      ? Array.from(footer.querySelectorAll('button')).map(b => b.textContent.trim()).filter(Boolean)
      : [];

    popups.push({ type: 'modal', title, body, buttons });
  }

  // 1b. Drawer-based confirmations (antd 5 pattern). Only treat as a blocking
  //     modal when its footer has OK/Cancel-style buttons, so the edit-side
  //     drawer (no such footer) is never misreported. Classified as type 'modal'
  //     so formatBlockingPopups/handlePopupConfirm handle it uniformly.
  for (const drawer of document.querySelectorAll('.ant-drawer')) {
    const content = drawer.querySelector('.ant-drawer-content');
    if (!content || !isElementVisible(content)) continue;
    const footer = drawer.querySelector('.ant-drawer-footer');
    const btnText = footer ? Array.from(footer.querySelectorAll('button')).map(b => b.textContent.trim()) : [];
    const isConfirm = btnText.some(t => /\b(ok|cancel|yes|no|confirm|submit)\b/i.test(t));
    if (!isConfirm) continue;
    const title = (drawer.querySelector('.ant-drawer-title, .ant-drawer-header-title')?.textContent || '').trim();
    const body = (drawer.querySelector('.ant-drawer-body')?.textContent || '').trim().substring(0, 300);
    popups.push({ type: 'modal', title, body, buttons: btnText });
  }

  // 2. Ant Design Popconfirm (small confirmation popovers)
  const popovers = document.querySelectorAll('.ant-popover:not(.ant-popover-hidden)');
  for (const popover of popovers) {
    const popconfirm = popover.querySelector('.ant-popconfirm');
    if (!popconfirm) continue;

    const titleEl = popconfirm.querySelector('.ant-popconfirm-title-text, .ant-popconfirm-message-text');
    const title = (titleEl?.textContent || '').trim();
    const buttonsContainer = popconfirm.querySelector('.ant-popconfirm-buttons');
    const buttons = buttonsContainer
      ? Array.from(buttonsContainer.querySelectorAll('button')).map(b => b.textContent.trim()).filter(Boolean)
      : [];

    popups.push({ type: 'popconfirm', title, body: '', buttons });
  }

  // 3. Notification banners and alerts (non-blocking, auto-dismiss targets)
  const notifications = document.querySelectorAll('.ant-notification-notice');
  for (const notice of notifications) {
    const msg = (notice.querySelector('.ant-notification-notice-message')?.textContent || '').trim();
    const desc = (notice.querySelector('.ant-notification-notice-description')?.textContent || '').trim();
    popups.push({ type: 'notification', title: msg, body: desc.substring(0, 200), buttons: [] });
  }

  const alerts = document.querySelectorAll('.ant-alert');
  for (const alertEl of alerts) {
    const msg = (alertEl.querySelector('.ant-alert-message')?.textContent || '').trim();
    const desc = (alertEl.querySelector('.ant-alert-description')?.textContent || '').trim();
    popups.push({ type: 'notification', title: msg, body: desc.substring(0, 200), buttons: [] });
  }

  // 4. Toast messages (antd Message)
  const toasts = document.querySelectorAll('.ant-message-notice');
  for (const toast of toasts) {
    const text = (toast.textContent || '').trim();
    popups.push({ type: 'toast', title: text, body: '', buttons: [] });
  }

  return popups;
}

/**
 * Format detected popups into a human-readable string for inclusion in tool results.
 * Only includes BLOCKING popups (modals, popconfirms) — toasts and notifications are
 * auto-dismissed silently.
 */
function formatBlockingPopups(popups) {
  const blocking = popups.filter(p => p.type === 'modal' || p.type === 'popconfirm');
  if (blocking.length === 0) return '';

  const lines = blocking.map((p, i) => {
    const label = p.type === 'modal' ? 'Modal' : 'Popconfirm';
    const titlePart = p.title ? `"${p.title}"` : '(no title)';
    const bodyPart = p.body ? ` — ${p.body.substring(0, 150)}` : '';
    const btnsPart = p.buttons.length > 0 ? ` [Buttons: ${p.buttons.join(', ')}]` : '';
    return `${i + 1}. [${label}] ${titlePart}${bodyPart}${btnsPart}`;
  });

  return `\n\n⚠️ ACTIVE POPUPS:\n${lines.join('\n')}`;
}

/**
 * Silently dismiss non-critical popups (notifications, alerts, toasts).
 * Does NOT touch modals or popconfirms — those need explicit LLM action.
 */
function autoDismissNonCriticalPopups() {
  // Close notification banners
  document.querySelectorAll('.ant-notification-notice-close').forEach(btn => {
    try { btn.click(); } catch (e) { /* ignore */ }
  });

  // Close alert banners (that have a close button)
  document.querySelectorAll('.ant-alert-close-icon, .ant-alert-close-text').forEach(btn => {
    try { btn.click(); } catch (e) { /* ignore */ }
  });

  // Toast messages auto-dismiss; no action needed
}


// ==================== Post-Navigation Search Helpers ====================

/**
 * Wait for data to load in the table after clicking Search.
 * Detects data by watching for table rows to appear or an empty-state placeholder.
 */
function waitForDataLoad(mainElement, timeoutMs = 8000) {
  return new Promise((resolve) => {
    const startTime = Date.now();

    const checkData = () => {
      const rows = mainElement.querySelectorAll(
        '.ant-table-tbody tr:not(.ant-table-placeholder)'
      );
      if (rows.length > 0) return true;
      const emptyText = mainElement.querySelector('.ant-empty-description, .ant-table-placeholder');
      if (emptyText) return true;
      return false;
    };

    // If data is already present, resolve quickly
    if (checkData()) {
      setTimeout(resolve, 500);
      return;
    }

    const observer = new MutationObserver(() => {
      if (checkData()) {
        observer.disconnect();
        setTimeout(resolve, 300);
      }
    });
    observer.observe(mainElement, { childList: true, subtree: true });

    // Poll as fallback — MutationObserver can miss some React updates
    const poll = setInterval(() => {
      if (checkData()) {
        clearInterval(poll);
        observer.disconnect();
        setTimeout(resolve, 300);
      } else if (Date.now() - startTime > timeoutMs) {
        clearInterval(poll);
        observer.disconnect();
        resolve();
      }
    }, 500);

    // Safety: clean up on timeout
    setTimeout(() => {
      clearInterval(poll);
      observer.disconnect();
      resolve();
    }, timeoutMs);
  });
}

/**
 * Trigger a BMS list page's data load and wait for the rows to appear.
 *
 * List pages (SearchBookingEnhance, SearchConsolidationEnhance, ...) only load
 * rows when antd Input.Search fires onSearch -> handleOnApplyFilter -> getData.
 * A synthetic element.click() on the search button does NOT reliably reach that
 * onSearch handler, so the grid stays empty and the agent loops retrying
 * different terms for minutes (root cause of the "show me a booking" failure:
 * the table is empty *until* Search truly fires, and a programmatic click never
 * fires it). Verified fix: walk the React 16 fiber from the search button up to
 * the list component class instance (e.g. BookingEnquiry) and call its getData()
 * directly — the same code path a real user click runs. Falls back to a click
 * for pages without a getData instance.
 *
 * Returns true if a search was triggered (and waited for), false otherwise.
 */
async function triggerAntdSearchAndLoad(mainElement, timeoutMs = 10000) {
  const main = mainElement || document.querySelector('main');
  if (!main) return false;
  const searchBtn = main.querySelector('.ant-input-search-button');

  // Preferred path: call the component's getData() directly via the React fiber.
  try {
    if (searchBtn) {
      const fiberKey = Object.keys(searchBtn).find(
        (k) => k.indexOf('__reactInternalInstance$') === 0
      );
      let fiber = fiberKey ? searchBtn[fiberKey] : null;
      let inst = null;
      let depth = 0;
      while (fiber && depth < 60) {
        if (fiber.stateNode && typeof fiber.stateNode.getData === 'function') {
          inst = fiber.stateNode;
          break;
        }
        fiber = fiber.return;
        depth += 1;
      }
      if (inst && typeof inst.getData === 'function') {
        console.log('[BMS Tools] Triggered list getData() via React fiber (depth ' + depth + ', ' + (inst.constructor && inst.constructor.name) + ')');
        try {
          inst.getData(function () {});
        } catch (e) {
          inst.getData();
        }
        await waitForDataLoad(main, timeoutMs);
        return true;
      }
    }
  } catch (e) {
    console.warn('[BMS Tools] fiber getData failed, falling back to click:', e.message);
  }

  // Fallback: synthetic click (for pages where onSearch fires from a click, or
  // where there is no getData instance).
  if (searchBtn && !searchBtn.disabled && !searchBtn.hasAttribute('disabled')) {
    searchBtn.click();
    await waitForDataLoad(main, timeoutMs);
    return true;
  }
  return false;
}

/**
 * After navigating to a list page, wait for the Search button to become
 * enabled (antd Input.Search enterButton), then trigger the data load.
 *
 * Why this is needed: Booking list pages (SearchBookingEnhance,
 * SearchConsolidationEnhance, etc.) do NOT auto-load data. The page's
 * componentDidMount sets searchBoxDisabled=true and only sets it to false
 * after async init (getCompsFilterSetting, getUserPreferenceList, etc.).
 * The data only loads when the Search button is clicked, which triggers
 * handleOnApplyFilter -> getData.
 *
 * Returns true if Search was triggered, false otherwise (e.g. no Search button).
 */
async function waitForSearchButtonAndClick(maxWaitMs = 10000) {
  const startTime = Date.now();
  const checkInterval = 500;

  while (Date.now() - startTime < maxWaitMs) {
    const main = document.querySelector('main');
    if (!main) {
      await new Promise(r => setTimeout(r, checkInterval));
      continue;
    }

    const searchBtn = main.querySelector('.ant-input-search-button');
    if (!searchBtn) {
      // Page might still be rendering — keep polling.
      // After maxWaitMs, we'll do a final check below to determine
      // if this is truly a non-list page (no Search button ever appears).
      await new Promise(r => setTimeout(r, checkInterval));
      continue;
    }

    // antd Input.Search disables via the disabled prop on the wrapper.
    // Check both the wrapper class and the button's own disabled attribute.
    const inputSearchWrapper = searchBtn.closest('.ant-input-search');
    const wrapperDisabled = inputSearchWrapper && (
      inputSearchWrapper.classList.contains('ant-input-search-disabled') ||
      inputSearchWrapper.querySelector('.ant-input-disabled')
    );

    if (!wrapperDisabled && !searchBtn.disabled && !searchBtn.hasAttribute('disabled')) {
      // Button is ready — trigger the list data load (getData via fiber, click fallback)
      console.log('[BMS Tools] Search button ready, triggering list data load');
      await triggerAntdSearchAndLoad(main, 10000);
      return true;
    }

    // Button exists but is still disabled — wait and retry
    await new Promise(r => setTimeout(r, checkInterval));
  }

  // Timed out waiting for the button to become enabled.
  // Try one last time anyway — maybe it just became ready.
  const main = document.querySelector('main');
  const searchBtn = main && main.querySelector('.ant-input-search-button');
  if (searchBtn && !searchBtn.disabled) {
    await triggerAntdSearchAndLoad(main, 10000);
    return true;
  }

  console.log('[BMS Tools] Timed out waiting for Search button to become enabled');
  return false;
}

// ==================== Antd Form Helpers ====================

/**
 * Detect the type of form control inside an antd FormItem.
 */
function detectFieldType(formItem) {
  if (!formItem) return 'unknown';
  // Shadowing widgets FIRST — they nest inside the generic classes below, so they must
  // be checked before .ant-select / .ant-picker / .ant-checkbox / <input>:
  if (formItem.querySelector('.ant-upload')) return 'upload';
  if (formItem.querySelector('.ant-cascader-picker') || formItem.querySelector('.ant-cascader')) return 'cascader';
  if (formItem.querySelector('.ant-select-tree')) return 'treeselect';
  if (formItem.querySelector('.ant-picker-range')) return 'rangepicker';
  if (formItem.querySelector('.ant-segmented')) return 'segmented';
  if (formItem.querySelector('.ant-rate')) return 'rate';
  if (formItem.querySelector('.ant-slider')) return 'slider';
  if (formItem.querySelector('.ant-transfer')) return 'transfer';
  if (formItem.querySelector('.ant-checkbox-group')) return 'checkboxgroup';
  // Generic controls:
  if (formItem.querySelector('.ant-select')) return 'select';
  if (formItem.querySelector('.ant-picker')) return 'datepicker';
  if (formItem.querySelector('.ant-checkbox')) return 'checkbox';
  if (formItem.querySelector('.ant-switch')) return 'switch';
  if (formItem.querySelector('.ant-radio-group')) return 'radio';
  if (formItem.querySelector('.ant-input-number')) return 'inputnumber';
  if (formItem.querySelector('textarea')) return 'textarea';
  if (formItem.querySelector('input')) return 'input';
  return 'unknown';
}

/**
 * Find form fields by matching antd FormItem label text.
 * Returns array of { formItem, control, label, type }.
 */
function findFormFieldsByLabel(labelText) {
  const results = [];
  if (!labelText) return results;

  const normalized = labelText.toLowerCase().trim();
  const formItems = document.querySelectorAll('.ant-form-item');

  for (const formItem of formItems) {
    const labelEl = formItem.querySelector('.ant-form-item-label label');
    if (!labelEl) continue;

    const label = labelEl.textContent.trim();
    if (!label.toLowerCase().includes(normalized)) continue;

    const controlContainer = formItem.querySelector('.ant-form-item-control');
    if (!controlContainer) continue;

    const type = detectFieldType(formItem);

    let control;
    if (type === 'select') control = formItem.querySelector('.ant-select');
    else if (type === 'datepicker') control = formItem.querySelector('.ant-picker');
    else if (type === 'checkbox') control = formItem.querySelector('.ant-checkbox-wrapper') || formItem.querySelector('.ant-checkbox');
    else if (type === 'switch') control = formItem.querySelector('.ant-switch');
    else if (type === 'radio') control = formItem.querySelector('.ant-radio-group');
    else if (type === 'inputnumber') control = formItem.querySelector('.ant-input-number');
    else if (type === 'textarea') control = formItem.querySelector('textarea');
    else if (type === 'input') control = formItem.querySelector('input');
    else control = controlContainer;

    results.push({ formItem, control, label, type });
  }

  return results;
}

/**
 * Find form fields by name/id attribute (fallback when label matching fails).
 */
function findFormFieldsByName(fieldName) {
  const results = [];
  if (!fieldName) return results;

  const inputs = document.querySelectorAll(
    `input[name="${fieldName}"], input[id*="${fieldName}"], textarea[name="${fieldName}"], select[name="${fieldName}"]`
  );

  for (const input of inputs) {
    const formItem = input.closest('.ant-form-item');
    if (!formItem) continue;

    const labelEl = formItem.querySelector('.ant-form-item-label label');
    const label = labelEl ? labelEl.textContent.trim() : '';
    const type = detectFieldType(formItem);
    const controlContainer = formItem.querySelector('.ant-form-item-control');

    results.push({ formItem, control: input, label, type });
  }

  return results;
}

/**
 * Set value on a native input and trigger React synthetic events.
 * Works for input, textarea, and InputNumber inner inputs.
 */
function setReactInputValue(element, value) {
  if (!element) return false;

  element.focus();
  element.setSelectionRange(0, element.value.length);

  const ok = document.execCommand('insertText', false, value);
  if (!ok) {
    // Fallback for browsers/contexts where execCommand is disabled
    const tagName = element.tagName.toLowerCase();
    const prototype = tagName === 'textarea'
      ? window.HTMLTextAreaElement.prototype
      : window.HTMLInputElement.prototype;
    const nativeSetter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
    if (nativeSetter) {
      nativeSetter.call(element, value);
    } else {
      element.value = value;
    }
    element.dispatchEvent(new Event('input', { bubbles: true }));
    element.dispatchEvent(new Event('change', { bubbles: true }));
  }
  return true;
}

/**
 * Select an option in an antd Select dropdown.
 * Handles both simple and searchable selects.
 */
function realWait(ms) {
  return new Promise(r => setTimeout(r, ms));
}

async function triggerAntdSelectChange(selectElement, targetValue, fieldLabel) {
  if (!selectElement) return false;

  const selector = selectElement.querySelector('.ant-select-selector');
  if (!selector) return false;

  // Defer outside any React render cycle
  await realWait(50);

  // Click to open dropdown
  selector.click();

  // Brief wait for dropdown to render
  await realWait(300);

  // Check if it's a searchable select
  const searchInput = selectElement.querySelector('.ant-select-selection-search-input');

  if (searchInput) {
    // Type the value to filter options
    searchInput.focus();
    setReactInputValue(searchInput, targetValue);

    // Also trigger React Fiber's onChange/onInput to ensure antd's onSearch fires.
    // Native events alone may not reach antd's internal handler for API-backed selects.
    const fiberKey = Object.keys(searchInput).find(k => k.startsWith('__reactFiber$') || k.startsWith('__reactInternalInstance$'));
    if (fiberKey) {
      let fiber = searchInput[fiberKey];
      for (let depth = 0; depth < 10 && fiber; depth++) {
        const props = fiber.memoizedProps || fiber.pendingProps;
        if (props) {
          if (props.onChange) {
            props.onChange({ target: searchInput, currentTarget: searchInput });
            break;
          }
          if (props.onInput) {
            props.onInput({ target: searchInput, currentTarget: searchInput });
            break;
          }
        }
        fiber = fiber.return;
      }
    }

    // Wait for API-backed search to respond and update options.
    await realWait(1500);
  } else {
    // No search input — try typing into the selector itself to trigger antd's built-in filter.
    // Some antd Select components filter options by typed text even without a visible search input.
    selector.focus();
    setReactInputValue(selector, targetValue);
    await realWait(800);
  }

  // Wait for an actual selectable option using MutationObserver (adaptive timing)
  const dropdown = await waitForElement(
    '.ant-select-dropdown:not(.ant-select-dropdown-hidden) .ant-select-item-option',
    5000
  );

  if (!dropdown) {
    // Check dropdown state AFTER timeout for diagnostics
    const postCheckDropdowns = document.querySelectorAll('.ant-select-dropdown:not(.ant-select-dropdown-hidden)');
    const postCheckEmpty = [];
    for (const dd of postCheckDropdowns) {
      const emptyEl = dd.querySelector('.ant-select-item-empty, .ant-empty-description');
      postCheckEmpty.push(emptyEl ? emptyEl.textContent : 'no-empty-msg');
    }
    console.log(`[triggerAntdSelectChange] FAIL: no option found. Visible dropdowns: ${postCheckDropdowns.length}, empty states: [${postCheckEmpty.join(', ')}]`);
    return false;
  }

  console.log(`[triggerAntdSelectChange] option found, proceeding to match`);

  // Retry matching: options may be stale initially while API is still fetching.
  const MATCH_RETRIES = 3;
  const MATCH_RETRY_DELAY = 1000;
  const target = targetValue.toLowerCase();

  for (let attempt = 0; attempt < MATCH_RETRIES; attempt++) {
    // Re-query dropdown and options each attempt (DOM may have updated)
    const dropdowns = document.querySelectorAll('.ant-select-dropdown:not(.ant-select-dropdown-hidden)');
    const matches = [];

    for (const dd of dropdowns) {
      const options = dd.querySelectorAll('.ant-select-item-option');

      for (const option of options) {
        const optionText = (option.textContent || '').trim().toLowerCase();
        const optionTitle = (option.getAttribute('title') || '').trim().toLowerCase();
        const displayText = optionText || optionTitle;

        // Skip if no match at all
        if (!displayText.includes(target)) {
          continue;
        }

        // Determine priority level
        let priority = 0;

        // Priority 1: Exact match (highest)
        if (displayText === target) {
          priority = 3;
        }
        // Priority 2: Starts with target AND target appears as a complete word/phrase
        // This distinguishes "DUMMY DATA - TESTING AGENT" from containing "CONSIGNEE"
        else if (displayText.startsWith(target) ||
                 displayText.startsWith(target + ' ') ||
                 displayText.startsWith(target + '(')) {
          // Additional check: if target contains distinguishing words (like "AGENT" vs "CONSIGNEE"),
          // prefer options where those distinguishing words also appear
          const targetWords = target.split(/\s+/).filter(w => w.length > 2); // Only meaningful words (>2 chars)
          let wordBonus = 0;
          for (const word of targetWords) {
            if (displayText.includes(word)) {
              wordBonus += 1;
            }
          }
          priority = 2 + (wordBonus * 0.1);
        }
        // Priority 3: Contains target anywhere
        else {
          // For contains matches, prefer options where more of the target words appear
          const targetWords = target.split(/\s+/).filter(w => w.length > 0);
          let matchCount = 0;
          for (const word of targetWords) {
            if (displayText.includes(word)) {
              matchCount += 1;
            }
          }
          // Only consider if at least 50% of words match
          if (matchCount / targetWords.length >= 0.5) {
            priority = 1 + (matchCount / targetWords.length);
          }
        }

        // Field label context: boost options whose text contains words from the field label
        // e.g. "Shipper" field → prefer "DUMMY DATA - TESTING SHIPPER" over "DUMMY DATA - TESTING CONSIGNEE"
        if (fieldLabel && priority > 0) {
          const labelLower = fieldLabel.toLowerCase();
          const labelWords = labelLower.split(/[\s\-_/]+/).filter(w => w.length > 2);
          for (const word of labelWords) {
            if (displayText.includes(word)) {
              priority += 0.15;
            }
          }
        }

        if (priority > 0) {
          matches.push({ option, priority, displayText });
        }
      }
    }

    // Sort by priority (descending). When priorities are equal, maintain document order (first match wins).
    matches.sort((a, b) => b.priority - a.priority);

    if (matches.length > 0) {
      matches[0].option.click();
      await realWait(200);
      return true;
    }

    // No match yet — options may still be loading from the API
    if (attempt < MATCH_RETRIES - 1) {
      const visibleCount = document.querySelectorAll('.ant-select-dropdown:not(.ant-select-dropdown-hidden) .ant-select-item-option').length;
      console.log(`[triggerAntdSelectChange] No match on attempt ${attempt + 1}/${MATCH_RETRIES} (${visibleCount} options visible), retrying in ${MATCH_RETRY_DELAY}ms...`);
      await realWait(MATCH_RETRY_DELAY);
    }
  }

  console.log(`[triggerAntdSelectChange] No matching option found after ${MATCH_RETRIES} attempts for "${targetValue}"`);
  return false;
}

// ==================== Tool Functions ====================

/**
 * Navigate to a page using React Router's history.push().
 * This triggers SPA navigation — no full page reload.
 */
async function navigateToPage({ page, so_number }) {
  if (so_number) {
    const nav = safePush(`/BMS/enhance_booking/search_booking/shipping_order/${so_number}`);
    if (!nav.confirmed) return _navCanceledResult(nav.message);
  } else {
    const route = ROUTE_MAP[page];
    if (!route) {
      return { content: [{ type: "text", text: `Unknown page: ${page}. Available: ${Object.keys(ROUTE_MAP).join(', ')}` }] };
    }
    const nav = safePush(route);
    if (!nav.confirmed) return _navCanceledResult(nav.message);
  }

  // Wait for SPA route change + React render
  await waitForRender(2000);

  // Auto-click Search button on list pages after the page is fully initialized.
  // This handles booking/consolidation/party list pages where data does not
  // auto-load — the user must click Search to trigger getData().
  const searchClicked = await waitForSearchButtonAndClick(10000);

  const url = window.location.pathname;
  const message = searchClicked
    ? `Navigated to ${url} (auto-searched)`
    : `Navigated to ${url}`;
  return { content: [{ type: "text", text: message }] };
}


/**
 * Read the currently visible page content.
 * Captures URL, breadcrumb, and main content area.
 */
async function readCurrentPage() {
  const url = window.location.pathname;
  const main = document.querySelector('main');

  // Safety net: if the page shows "Please either apply... click 'Search'"
  // it means data hasn't been loaded yet. Click Search and wait for data.
  if (main) {
    const pageText = main.innerText || '';
    if (pageText.includes("click 'Search'") || pageText.includes("click \"Search\"") ||
        pageText.includes("Please either apply")) {
      console.log('[BMS Tools] readCurrentPage: page needs Search click, clicking now...');
      const searchBtn = main.querySelector('.ant-input-search-button');
      if (searchBtn) {
        searchBtn.click();
        // Wait for data to load
        await new Promise(r => setTimeout(r, 5000));
      }
    }
  }

  // Auto-dismiss non-critical popups silently before reading
  autoDismissNonCriticalPopups();

  // Try <main> first (standard layout), fallback to body
  const content = main
    ? main.innerText?.substring(0, 8000) || ''
    : document.body.innerText?.substring(0, 8000) || '';

  // Detect any remaining blocking popups and include in result
  const popups = detectPopups();
  const popupInfo = formatBlockingPopups(popups);

  return {
    content: [{ type: "text", text: `URL: ${url}\n\n${content}${popupInfo}` }]
  };
}


/**
 * Click a button by its text content or aria-label.
 */

// State-changing buttons whose click outcome must be VERIFIED before reporting.
// In BMS, SO approval is submitted via the form "Save" button (there is NO
// "Approve" button), so "Save"/"Submit" are the real approval trigger words.
export const ACTION_VERIFY_BUTTONS = /^(save|submit|approve|approved|reject|rejected|confirm|delete|remove|ok|yes|release|send|publish|approve all|reject all|save & send|save & close)$/i;
// NOTE: "apply" was removed - the only BMS "Apply" button is the filter-bar Apply (a
// READ), so gating it caused a false Accept prompt on "show me bookings filtered by X".

/**
 * After a state-changing click, capture the real outcome (success toast / error
 * toast / persistent error notification / confirm modal) so the tool result
 * carries EVIDENCE instead of the blind "Clicked button: X" (M5: false success).
 * Only consumes toasts/notifications that appeared AFTER the click (pre-count +
 * dataset flag), distinguishing success vs error by antd icon classes. Modals
 * are detected via detectPopups() (broadened, M1). Runs BEFORE auto-dismiss so
 * the signal is not destroyed before the LLM sees it.
 */
async function captureActionResult(buttonName, timeoutMs = 6000) {
  const q = (s) => Array.from(document.querySelectorAll(s));
  const beforeNotif = q('.ant-notification-notice').length;
  const beforeToast = q('.ant-message-notice').length;
  const start = Date.now();
  const fresh = (el) => el && !el.dataset.__bmsCaptured;
  const isErrorIcon = (el) => el.querySelector('.anticon-close-circle, .anticon-exclamation-circle') || el.matches('.ant-message-error, .ant-notification-notice-error');
  const isSuccessIcon = (el) => el.querySelector('.anticon-check-circle, .anticon-check') || el.matches('.ant-message-success, .ant-notification-notice-success');

  while (Date.now() - start < timeoutMs) {
    await realWait(150);

    // (1) Persistent error notification (bottomRight, duration:0) — BMS's primary
    //     approval-validation failure signal (missing sailing schedule/rate, etc.).
    let newNotif = q('.ant-notification-notice').slice(beforeNotif).find(fresh);
    if (!newNotif) {
      newNotif = q('.ant-notification-notice').find(n => isErrorIcon(n) && fresh(n));
    }
    if (newNotif) {
      newNotif.dataset.__bmsCaptured = '1';
      return { outcome: 'error', evidence: (newNotif.textContent || '').trim().substring(0, 240) };
    }

    // (2) Error toast.
    const errToast = q('.ant-message-notice').find(t => (isErrorIcon(t)) && fresh(t));
    if (errToast) {
      errToast.dataset.__bmsCaptured = '1';
      return { outcome: 'error', evidence: (errToast.textContent || '').trim() };
    }

    // (3) Success toast.
    let okToast = q('.ant-message-notice').slice(beforeToast).find(fresh);
    if (!okToast) {
      okToast = q('.ant-message-notice').find(t => isSuccessIcon(t) && fresh(t));
    }
    if (okToast) {
      okToast.dataset.__bmsCaptured = '1';
      return { outcome: 'success', evidence: (okToast.textContent || '').trim() };
    }

    // (4) A confirm/warning modal (do NOT auto-click it — the LLM decides).
    const blocking = detectPopups().filter(p => p.type === 'modal' || p.type === 'popconfirm');
    if (blocking.length > 0) {
      const m = blocking[0];
      const ev = `"${m.title || '(no title)'}"${m.body ? ' - ' + m.body : ''}${m.buttons.length ? ' [Buttons: ' + m.buttons.join(', ') + ']' : ''}`;
      return { outcome: 'confirm_popup', evidence: ev };
    }
  }
  return { outcome: 'unknown', evidence: 'no success/error message or popup detected within timeout' };
}

async function clickButton({ button_name }) {
  // Special case: clicking "Search" on a list page must fire the component's
  // getData() (via the React fiber), because a synthetic button click does NOT
  // trigger antd Input.Search onSearch and would leave the grid empty. Fall
  // through to normal click matching if there is no antd search button here.
  if (/^search$/i.test((button_name || '').trim())) {
    const main = document.querySelector('main');
    if (main && main.querySelector('.ant-input-search-button')) {
      const triggered = await triggerAntdSearchAndLoad(main, 10000);
      if (triggered) {
        return { content: [{ type: "text", text: `Clicked button: ${button_name} (list data loaded)` }] };
      }
    }
  }

  // Case-insensitive matching: BMS buttons are capitalized ("Edit", "Save") but
  // the LLM often passes lowercase ("edit", "save"). Case-sensitive compares
  // silently miss them and return "Button not found" (root cause of the
  // "button not found: edit" failure), so normalize both sides to lowercase.
  const bn = (button_name || '').trim().toLowerCase();
  const buttons = document.querySelectorAll('button, [role="button"], input[type="submit"]');
  for (const btn of buttons) {
    // Skip hidden buttons (e.g., notification drawer buttons)
    if (btn.offsetParent === null) continue;
    const text = btn.textContent?.trim();
    const ariaLabel = btn.getAttribute('aria-label');
    const value = btn.getAttribute('value');
    const t = (text || '').toLowerCase();
    const al = (ariaLabel || '').toLowerCase();
    const v = (value || '').toLowerCase();
    if (t === bn || t.includes(bn) ||
        al === bn || al.includes(bn) ||
        v === bn) {
      btn.click();
      // For state-changing buttons, verify the real outcome (M5) — this also
      // waits long enough for a late async confirm modal to mount (M1).
      if (ACTION_VERIFY_BUTTONS.test((button_name || '').trim())) {
        const r = await captureActionResult(button_name);
        return { content: [{ type: "text", text: `Clicked button: ${button_name}. RESULT: ${r.outcome} (${r.evidence})` }] };
      }
      await waitForRender();
      return { content: [{ type: "text", text: `Clicked button: ${button_name}` }] };
    }
  }
  // Also check <a> elements styled as buttons
  const links = document.querySelectorAll('a.btn, a[role="button"]');
  for (const link of links) {
    const lt = (link.textContent || '').trim().toLowerCase();
    if (lt === bn || lt.includes(bn)) {
      link.click();
      if (ACTION_VERIFY_BUTTONS.test((button_name || '').trim())) {
        const r = await captureActionResult(button_name);
        return { content: [{ type: "text", text: `Clicked link-button: ${button_name}. RESULT: ${r.outcome} (${r.evidence})` }] };
      }
      await waitForRender();
      return { content: [{ type: "text", text: `Clicked link-button: ${button_name}` }] };
    }
  }
  return { content: [{ type: "text", text: `Button not found: ${button_name}` }] };
}


/**
 * Click a list item (e.g., an SO# link in search results).
 */
async function clickListItem({ identifier }) {
  // Try <a> elements first (SO# links, party links)
  const links = document.querySelectorAll('a');
  for (const link of links) {
    if (link.textContent?.trim() === identifier) {
      link.click();
      await waitForRender(2000);
      return { content: [{ type: "text", text: `Clicked item: ${identifier}` }] };
    }
  }
  // Try table cells
  const cells = document.querySelectorAll('td');
  for (const cell of cells) {
    if (cell.textContent?.trim() === identifier) {
      cell.click();
      await waitForRender(2000);
      return { content: [{ type: "text", text: `Clicked cell: ${identifier}` }] };
    }
  }
  return { content: [{ type: "text", text: `Item not found: ${identifier}` }] };
}


/**
 * Switch to a tab on the Shipping Order detail page.
 */
async function switchSoTab({ tab_name }) {
  const findTabByName = () => {
    const tabs = document.querySelectorAll('[role="tab"]');
    for (const tab of tabs) {
      if (tab.textContent?.trim() === tab_name) {
        return tab;
      }
    }
    const tabLike = document.querySelectorAll('.nav-link, .tab, [data-tab]');
    for (const el of tabLike) {
      if (el.textContent?.trim() === tab_name) {
        return el;
      }
    }
    return null;
  };

  const waitForTabByName = (timeoutMs = 5000) => new Promise((resolve) => {
    const existingTab = findTabByName();
    if (existingTab) {
      resolve(existingTab);
      return;
    }

    const observer = new MutationObserver(() => {
      const nextTab = findTabByName();
      if (nextTab) {
        observer.disconnect();
        resolve(nextTab);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    setTimeout(() => {
      observer.disconnect();
      resolve(findTabByName());
    }, timeoutMs);
  });

  const targetTab = await waitForTabByName();
  if (targetTab) {
    targetTab.click();
    await waitForRender();
    return { content: [{ type: "text", text: `Switched to tab: ${tab_name}` }] };
  }

  return { content: [{ type: "text", text: `Tab not found: ${tab_name}. Available tabs may not be loaded.` }] };
}


/**
 * Read the content of a specific tab on the SO detail page.
 * Switches to the tab first, then reads the panel content.
 */
async function readSoTab({ tab_name }) {
  // Guard: if no SO is open (e.g. open_shipping_order landed on the /AccessDeclined
  // 403 page because the SO number was mistyped), there are no tabs to read.
  // Return a clear error instead of a misleading "Tab not found: <name>".
  const _soUrl = window.location.pathname;
  const _soTitle = document.title || '';
  if (/\/AccessDeclined/i.test(_soUrl) || /403/i.test(_soTitle) || !/\/shipping_order\//i.test(_soUrl)) {
    const _where = /403/i.test(_soTitle) ? '403 Forbidden' : _soUrl;
    return { content: [{ type: "text", text: `ERROR: No Shipping Order is currently open (page is "${_where}"). open_shipping_order must succeed before reading a tab; the requested SO number may be mistyped or inaccessible. Do NOT retry read_so_tab.` }] };
  }

  const isVisible = (element) => {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    return !element.hidden
      && element.getAttribute('aria-hidden') !== 'true'
      && style.display !== 'none'
      && style.visibility !== 'hidden';
  };

  const getPanelText = (panel) => {
    if (!panel) return '';
    return (panel.innerText || panel.textContent || '').trim();
  };

  const normalizeCellText = (value) => (value || '').replace(/\s+/g, ' ').trim();

  const getStatusDataRows = (panel) => {
    if (!panel) {
      return [];
    }

    const headerLabels = new Set(['SO#', 'Status', 'Action', 'Remark', 'User', 'Updated Time']);

    return Array.from(panel.querySelectorAll('table tr'))
      .map((row) => {
        const cells = Array.from(row.querySelectorAll('th, td'))
          .map((cell) => {
            const value = normalizeCellText(cell.innerText || cell.textContent);
            return value === 'View History' ? '' : value;
          });
        const nonEmptyCells = cells.filter(Boolean);
        return {
          cells,
          nonEmptyCells,
          rowText: normalizeCellText(row.innerText || row.textContent)
        };
      })
      .filter(({ nonEmptyCells, rowText }) => {
        if (!nonEmptyCells.length || !rowText || /^no data$/i.test(rowText)) {
          return false;
        }

        const isHeaderRow = nonEmptyCells.every((cell) => headerLabels.has(cell));
        if (isHeaderRow) {
          return false;
        }

        return nonEmptyCells.length >= 2 || /\d{4}-\d{2}-\d{2}/.test(rowText);
      })
      .map(({ cells }) => {
        const dataCells = cells.length >= 7 ? cells.slice(1) : cells;
        const paddedCells = [...dataCells];
        while (paddedCells.length < 6) {
          paddedCells.push('');
        }
        return paddedCells.slice(0, 6);
      });
  };

  const waitForStatusRows = async (panel, timeoutMs = 10000) => {
    const startTime = Date.now();
    let latestRows = getStatusDataRows(panel);

    while (!latestRows.length && (Date.now() - startTime) < timeoutMs) {
      await waitForRender(500);
      latestRows = getStatusDataRows(panel);
    }

    return latestRows;
  };

  const waitForPanelContent = (panel, timeoutMs = 4000) => new Promise((resolve) => {
    const existingText = getPanelText(panel);
    if (existingText) {
      resolve(existingText);
      return;
    }

    const observer = new MutationObserver(() => {
      const nextText = getPanelText(panel);
      if (nextText) {
        observer.disconnect();
        resolve(nextText);
      }
    });

    observer.observe(panel, { childList: true, subtree: true, characterData: true });

    setTimeout(() => {
      observer.disconnect();
      resolve(getPanelText(panel));
    }, timeoutMs);
  });

  const formatStatusTab = (statusRows, fallbackText) => {
    if (!statusRows.length) {
      return fallbackText;
    }

    const historyLines = statusRows.map((cells) => {
      const [soNumber, status, action, remark, user, updatedTime] = cells;
      const displayStatus = status || action;
      const parts = [updatedTime, user].filter(Boolean);
      if (displayStatus) {
        parts.push(`Status: ${displayStatus}`);
      }
      if (action && action !== displayStatus) {
        parts.push(`Action: ${action}`);
      }
      if (remark) {
        parts.push(`Remark: ${remark}`);
      }
      return `- ${parts.join(' | ')}`;
    });

    const explicitStatuses = statusRows
      .map((cells) => cells[1] || cells[2])
      .filter(Boolean);

    const latestExplicitStatus = explicitStatuses[0] || 'Not explicitly shown';

    return [
      'Status History:',
      `Latest explicit status shown in history: ${latestExplicitStatus}`,
      ...historyLines
    ].join('\n');
  };

  const findTabByName = () => {
    const tabs = document.querySelectorAll('[role="tab"]');
    for (const tab of tabs) {
      if (tab.textContent?.trim() === tab_name) {
        return tab;
      }
    }

    const tabLike = document.querySelectorAll('.nav-link, .tab, [data-tab]');
    for (const el of tabLike) {
      if (el.textContent?.trim() === tab_name) {
        return el;
      }
    }

    return null;
  };

  const waitForTabByName = (timeoutMs = 5000) => new Promise((resolve) => {
    const existingTab = findTabByName();
    if (existingTab) {
      resolve(existingTab);
      return;
    }

    const observer = new MutationObserver(() => {
      const nextTab = findTabByName();
      if (nextTab) {
        observer.disconnect();
        resolve(nextTab);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    setTimeout(() => {
      observer.disconnect();
      resolve(findTabByName());
    }, timeoutMs);
  });

  const resolvePanelForTab = (tab) => {
    if (!tab) return null;

    const controls = tab.getAttribute('aria-controls');
    if (controls) {
      const controlledPanel = document.getElementById(controls);
      if (controlledPanel) {
        return controlledPanel;
      }
    }

    const tabId = tab.id;
    if (tabId) {
      const labelledPanel = document.querySelector(`[role="tabpanel"][aria-labelledby="${tabId}"]`);
      if (labelledPanel) {
        return labelledPanel;
      }
    }

    const visiblePanels = Array.from(document.querySelectorAll('[role="tabpanel"]')).filter(isVisible);
    if (visiblePanels.length === 1) {
      return visiblePanels[0];
    }

    const activePanel = document.querySelector('.tab-content .active, .tab-pane.active');
    if (activePanel) {
      return activePanel;
    }

    return visiblePanels[0] || document.querySelector('[role="tabpanel"]');
  };

  // Switch to the tab first
  const targetTab = await waitForTabByName();
  if (!targetTab) {
    return { content: [{ type: "text", text: `Tab not found: ${tab_name}` }] };
  }

  targetTab.click();

  // Wait for React to render the tab panel
  await waitForRender(1500);

  // Read the selected tab's panel rather than the first tabpanel in the DOM.
  const selectedTab = Array.from(document.querySelectorAll('[role="tab"]')).find(
    (tab) => tab.getAttribute('aria-selected') === 'true' && tab.textContent?.trim() === tab_name
  ) || targetTab;
  const panel = resolvePanelForTab(selectedTab);

  const statusRows = panel && tab_name === 'Status'
    ? await waitForStatusRows(panel)
    : [];
  let content;
  if (panel && tab_name === 'Status') {
    const statusPanelText = await waitForPanelContent(panel, 10000);
    content = formatStatusTab(statusRows, statusPanelText?.substring(0, 8000) || 'No content visible').substring(0, 8000);
  } else if (panel) {
    // Form tab: read field VALUES (innerText misses <input>/<textarea> .value) after
    // letting party Select labels + input values settle.
    await waitForFormStable(panel);
    content = (readFormFieldsText(panel) || (await waitForPanelContent(panel, 4000))).substring(0, 8000) || 'No content visible';
  } else {
    content = 'No content visible';
  }

  return {
    content: [{ type: "text", text: `Tab: ${tab_name}\n\n${content}` }]
  };
}


/**
 * Read the header information of the currently open Shipping Order.
 */
async function readSoHeader() {
  // Validate we're on a Shipping Order detail page before reading.
  const url = window.location.pathname;
  const isSoPage = /\/shipping_order\/S?\d+/i.test(url);
  if (!isSoPage) {
    return {
      content: [{ type: "text", text: 'ERROR: No Shipping Order is currently open. Please open a shipping order first (e.g. open_shipping_order or search_booking) before reading the header.' }]
    };
  }

  // Find the Header tab. Only click it if it isn't already active — re-clicking an
  // active tab re-mounts the panel and makes party/agent Selects flash their raw VALUE
  // (a party ID) before async-resolving to the label, which readSoHeader would capture.
  const tabs = document.querySelectorAll('[role="tab"]');
  const headerTab = Array.from(tabs).find(t => t.textContent?.trim() === 'Header');
  if (!headerTab) {
    return { content: [{ type: "text", text: 'Header tab not found on this page.' }] };
  }

  const headerActive = headerTab.getAttribute('aria-selected') === 'true' ||
                       headerTab.classList.contains('ant-tabs-tab-active');
  if (!headerActive) {
    headerTab.click();
    await waitForRender(1500);
  }

  // Resolve the Header tab's panel element
  const getHeaderPanel = () => {
    const controls = headerTab.getAttribute('aria-controls');
    if (controls) return document.getElementById(controls);
    const panel = document.querySelector('[role="tabpanel"]:not([hidden])') ||
                  document.querySelector('.ant-tabs-tabpane-active');
    return panel;
  };

  // Wait for form values to populate AND for Select labels to STABILIZE.
  // Party/agent Selects render their raw VALUE (a party ID, e.g. "978437") first,
  // then async-resolve to the display label (e.g. "DEMO CONSIGNEE 1 (4DEMOCOHKG)").
  // Breaking as soon as ANY select has text captures the raw ID. Instead, poll until
  // the set of selection-item texts stops changing for two consecutive polls, meaning
  // the labels have resolved. Timeout guards against pages that never resolve.
  // Two race modes to avoid (on top of the label-resolution race described above):
  //   (a) Reading while the form is still EMPTY — the party fetch lands late, so
  //       “stable” on an empty form means nothing. Only accept the form once at
  //       least one Select is populated (a saved SO always has Shipper/Consignee);
  //       plain input values alone are NOT enough evidence of a loaded form.
  //   (b) Breaking early because an INPUT populated while the Selects were still
  //       pending — the signature used to cover Selects only, so an empty Select
  //       set looked “stable”. Inputs are part of the signature now, so a late
  //       populating field keeps the loop alive.
  const maxWait = 10000;
  const startTime = Date.now();
  let prevSig = null;
  let stableRounds = 0;
  let sawPopulatedSelect = false;

  const snapshot = (panel) => {
    const selectItems = panel.querySelectorAll('.ant-select-selection-item');
    const inputs = panel.querySelectorAll('.ant-form-item input');
    const selectTexts = Array.from(selectItems).map(el => (el.textContent || '').trim());
    const inputVals = Array.from(inputs).map(el => (el.value || '').trim());
    return {
      populatedSelects: selectTexts.filter(t => t.length > 0).length,
      populatedInputs: inputVals.filter(v => v.length > 0).length,
      //  separator: concatenating raw values would let adjacent fields blur
      // into one another and fake stability (same trick the original code used).
      sig: selectTexts.join('') + '|' + inputVals.join(''),
    };
  };

  while (Date.now() - startTime < maxWait) {
    const panel = getHeaderPanel();
    if (panel) {
      const snap = snapshot(panel);
      if (snap.populatedSelects >= 1) sawPopulatedSelect = true;
      if (snap.populatedSelects >= 1 || snap.populatedInputs >= 3) {
        if (snap.sig === prevSig) {
          stableRounds += 1;
          if (stableRounds >= 2) break;  // unchanged across 2 polls -> labels resolved
        } else {
          stableRounds = 0;
        }
        prevSig = snap.sig;
      }
    }
    await waitForRender(300);
  }

  // Still no populated Select after the wait — the party fetch can land just
  // past the window. One delayed re-check before concluding the form is empty.
  if (!sawPopulatedSelect) {
    await waitForRender(2000);
    const panel2 = getHeaderPanel();
    if (panel2 && snapshot(panel2).populatedSelects >= 1) {
      // Data landed during the grace wait — let the labels settle before reading.
      await waitForRender(600);
    }
  }

  // Read the panel text directly — do NOT delegate to readSoTab which would
  // re-click the tab and trigger a re-render, losing the loaded data.
  const panel = getHeaderPanel();
  const panelText = (readFormFieldsText(panel) || (panel ? (panel.innerText || '').trim() : '') || 'No content visible').substring(0, 8000);

  return {
    content: [{ type: "text", text: `Tab: Header\n\n${panelText}` }]
  };
}


/**
 * Best-effort lookup of SO numbers similar to one that just failed to open.
 * Used by openShippingOrder when the SO redirects to /AccessDeclined, so a typo
 * (e.g. SHK-BNEVA-1421 instead of the real SHK-NBEVA-1421) surfaces a useful
 * suggestion instead of a dead-end "contact support" message.
 *
 * Searches the trailing numeric segment of the SO number on the booking list
 * (robust to letter transpositions — the tail is usually intact) and returns up
 * to 5 matching SO numbers, excluding the exact failed number.
 */
async function findSimilarShippingOrders(so_number) {
  try {
    const tail = (so_number.match(/(\d+)\s*$/) || [])[1] || so_number;
    const nav = safePush('/BMS/enhance_booking/search_booking');
    if (!nav.confirmed) return [];  // user stayed on the edited page; cannot search
    await waitForRender(2000);

    const main = document.querySelector('main');
    if (!main) return [];

    // The booking list's main search box is input.ant-input[placeholder="Search"].
    // Do NOT use .ant-select-selection-search-input here — those are per-column
    // filters and do not drive the list query.
    const searchInput = main.querySelector('input.ant-input[placeholder="Search"]');
    if (searchInput) {
      const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
      searchInput.focus();
      setter.call(searchInput, tail);
      searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      searchInput.dispatchEvent(new Event('change', { bubbles: true }));
      // Fire Enter so antd Input.Search onSearch updates the component's search
      // term before getData() reads it. Setting the DOM value alone does NOT
      // update React state, so getData would query an empty term.
      ['keydown', 'keypress', 'keyup'].forEach(function (type) {
        searchInput.dispatchEvent(new KeyboardEvent(type, { key: 'Enter', code: 'Enter', keyCode: 13, which: 13, bubbles: true }));
      });
      await realWait(600);
    }

    // Trigger the list query via the component's getData() — a synthetic click
    // does not fire antd Input.Search onSearch. Same React-fiber walk used by
    // triggerAntdSearchAndLoad.
    const searchBtn = main.querySelector('.ant-input-search-button');
    if (searchBtn) {
      const fiberKey = Object.keys(searchBtn).find(
        (k) => k.indexOf('__reactInternalInstance$') === 0 || k.startsWith('__reactFiber$')
      );
      let fiber = fiberKey ? searchBtn[fiberKey] : null;
      let depth = 0;
      while (fiber && depth < 60) {
        if (fiber.stateNode && typeof fiber.stateNode.getData === 'function') {
          try { fiber.stateNode.getData(function () {}); } catch (e) { fiber.stateNode.getData(); }
          break;
        }
        fiber = fiber.return;
        depth += 1;
      }
    }
    await waitForDataLoad(main, 8000);

    const matches = Array.from(main.querySelectorAll('td, a[href*="shipping_order"]'))
      .map((el) => (el.textContent || '').trim())
      .filter((t) => /^SHK-|^S\d{6}/i.test(t));
    return Array.from(new Set(matches))
      .filter((m) => m !== so_number)
      .slice(0, 5);
  } catch (e) {
    console.warn('[BMS Tools] findSimilarShippingOrders failed:', e.message);
    return [];
  }
}


/**
 * Open a shipping order by navigating to its detail page.
 */
async function openShippingOrder({ so_number }) {
  // Navigate to the SO detail page
  const nav = safePush(`/BMS/enhance_booking/search_booking/shipping_order/${so_number}`);
  if (!nav.confirmed) return _navCanceledResult(nav.message);
  await waitForRender(2000);

  // The SPA's access-check redirect to /AccessDeclined (HTTP 403) is ASYNC and
  // can take several seconds to fire, so a single URL snapshot right after the
  // push mis-reads a forbidden/non-existent SO as successfully opened. Poll
  // until the page settles: the SO's tabs render (real success) or the URL/title
  // flip to /AccessDeclined / "403" (failure). Timeout -> treat as not opened.
  let url = window.location.pathname;
  let title = document.title || '';
  const settled = await new Promise(function (resolve) {
    const start = Date.now();
    (function check() {
      url = window.location.pathname;
      title = document.title || '';
      const declined = /\/AccessDeclined/i.test(url) || /403/i.test(title);
      const tabsReady = /\/shipping_order\//i.test(url) && !declined
        && document.querySelectorAll('[role="tab"]').length > 0;
      if (declined) return resolve('declined');
      if (tabsReady) return resolve('ok');
      if (Date.now() - start > 6000) return resolve('timeout');
      setTimeout(check, 400);
    })();
  });

  // Detect access-decline / not-found. The SPA redirects unknown or forbidden
  // SO numbers to /AccessDeclined (HTTP 403). Previously this returned a SILENT
  // SUCCESS ("Opened shipping order X at /AccessDeclined"), which made the agent
  // proceed to read_so_tab (-> "Tab not found: Booking Approval") and
  // misdiagnose a typo in the SO number as a permission error, dead-ending the
  // user at "contact support". Instead: surface a clear ERROR and run a
  // best-effort search for similar SO numbers so the user gets a useful hint.
  const landedOnSoPage = settled === 'ok';
  if (!landedOnSoPage) {
    const where = /403/i.test(title) ? '403 Forbidden' : url;
    const suggestions = await findSimilarShippingOrders(so_number);
    let text = `ERROR: Could not open shipping order ${so_number}. The record does not exist or this account cannot access it (page redirected to "${where}"). This is most often a typo in the SO number.`;
    if (suggestions.length > 0) {
      const likely = suggestions.length === 1 ? ` Most likely: ${suggestions[0]}.` : '';
      text += ` I searched and found ${suggestions.length} similar SO${suggestions.length > 1 ? 's' : ''}: ${suggestions.join(', ')}.${likely} STOP here: do NOT open, approve, or call any other tool on this SO (no open_shipping_order / read_so_tab / read_so_header / click_button). Reply to the user that the requested SO was NOT found, show the closest match${suggestions.length === 1 ? ' (' + suggestions[0] + ')' : ''}, and ask them to confirm the exact SO number before you proceed with ANY action.`;
    } else {
      text += ` Do NOT call read_so_tab or read_so_header (no SO is open). Ask the user to double-check the SO number.`;
    }
    return { content: [{ type: "text", text }] };
  }

  return { content: [{ type: "text", text: `Opened shipping order ${so_number} at ${url}` }] };
}


/**
 * Apply a custom filter on the current list page.
 */
async function applyCustomFilter({ field, operator, value }) {
  // Step 1: Open the filter bar if not already visible
  let container = document.querySelector('.filterBarContainer');
  if (!container) {
    const filterIcon = document.querySelector('[class*="anticon-filter"]');
    if (!filterIcon) {
      return { content: [{ type: "text", text: 'Filter button not found on this page' }] };
    }
    const filterBtn = filterIcon.closest('button');
    if (!filterBtn) {
      return { content: [{ type: "text", text: 'Filter button not found on this page' }] };
    }
    filterBtn.click();
    container = await waitForElement('.filterBarContainer', 2000);
    if (!container) {
      return { content: [{ type: "text", text: 'Filter panel did not open' }] };
    }
    await waitForRender(300);
  }

  // Step 2: Ensure at least one clause row exists
  const clausesContainer = container.querySelector('.filterBarClausesContainer');
  let clauseRows = clausesContainer ? clausesContainer.querySelectorAll('.filterBarRowDiv') : [];
  if (clauseRows.length === 0) {
    const addIcon = container.querySelector('[class*="anticon-plus"]');
    if (addIcon) {
      const addBtn = addIcon.closest('button');
      if (addBtn) addBtn.click();
      await waitForRender(500);
    }
    clauseRows = clausesContainer ? clausesContainer.querySelectorAll('.filterBarRowDiv') : [];
    if (clauseRows.length === 0) {
      return { content: [{ type: "text", text: 'Could not add filter clause' }] };
    }
  }

  // Step 3: Select the field (1st ant-select in clause row)
  if (field) {
    const row = clausesContainer.querySelector('.filterBarRowDiv');
    const selects = row.querySelectorAll('.ant-select');
    if (selects.length > 0) {
      await triggerAntdSelectChange(selects[0], field);
      await waitForRender(600);
    }
  }

  // Step 4: Select the operator (2nd ant-select, re-query after field re-render)
  if (operator) {
    const row = clausesContainer.querySelector('.filterBarRowDiv');
    const selects = row.querySelectorAll('.ant-select');
    if (selects.length >= 2) {
      await triggerAntdSelectChange(selects[1], operator);
      await waitForRender(400);
    }
  }

  // Step 5: Fill the value (3rd element in clause row, re-query after operator re-render)
  let valueSet = false;
  if (value) {
    const row = clausesContainer.querySelector('.filterBarRowDiv');
    const cols = row.querySelectorAll('.ant-col');
    // Last col (index 3) is the value column
    if (cols.length >= 4) {
      const valueCol = cols[3];

      // Wait for value input to become enabled (React enables it after field+operator are set)
      const valueInput = valueCol.querySelector('input');
      if (valueInput && valueInput.disabled) {
        await new Promise(resolve => {
          const check = () => {
            if (!valueInput.disabled) return resolve();
            requestAnimationFrame(check);
          };
          setTimeout(() => resolve(), 3000);
          check();
        });
      }

      // Detect value input type
      const select = valueCol.querySelector('.ant-select');
      const picker = valueCol.querySelector('.ant-picker');
      const numberInput = valueCol.querySelector('.ant-input-number-input');
      const textInput = valueCol.querySelector('input');

      if (select) {
        await triggerAntdSelectChange(select, value);
        valueSet = true;
      } else if (picker) {
        const pickerInput = picker.querySelector('input');
        if (pickerInput) {
          setReactInputValue(pickerInput, value);
          pickerInput.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
          valueSet = true;
        }
      } else if (numberInput) {
        setReactInputValue(numberInput, value);
        numberInput.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
        valueSet = true;
      } else if (textInput) {
        setReactInputValue(textInput, value);
        valueSet = true;
      }
    }
    await waitForRender(300);
  }

  // Step 6: Click Apply (button is a sibling of filterBarContainer, not inside it)
  const applyBtn = container.parentElement.querySelector('button.filterBarButton');
  if (applyBtn && !applyBtn.disabled) {
    applyBtn.click();
    await waitForRender(2000);
  }

  // Step 7: Trigger Search to execute the filtered query. Call the component's
  // getData() via the React fiber — synthetic clicks do not fire antd onSearch.
  const main = document.querySelector('main');
  await triggerAntdSearchAndLoad(main, 10000);

  return {
    content: [{ type: "text", text: valueSet || !value
      ? `Applied filter: ${field || '(existing)'} ${operator || 'Like'} ${value || '(existing)'}`
      : `Could not set filter value for field "${field}". The field type may not support text input.` }]
  };
}


/**
 * Apply multiple filters at once — adds a row per filter, clicks Apply once at the end.
 * PREFERRED over applyCustomFilter when more than one filter is needed.
 */
async function applyFilters({ filters }) {
  if (!filters || filters.length === 0) {
    return { content: [{ type: "text", text: 'No filters provided' }] };
  }

  // Step 1: Open the filter bar if not already visible
  let container = document.querySelector('.filterBarContainer');
  if (!container) {
    const filterIcon = document.querySelector('[class*="anticon-filter"]');
    if (!filterIcon) {
      return { content: [{ type: "text", text: 'Filter button not found on this page' }] };
    }
    const filterBtn = filterIcon.closest('button');
    if (!filterBtn) {
      return { content: [{ type: "text", text: 'Filter button not found on this page' }] };
    }
    filterBtn.click();
    container = await waitForElement('.filterBarContainer', 2000);
    if (!container) {
      return { content: [{ type: "text", text: 'Filter panel did not open' }] };
    }
    await waitForRender(300);
  }

  const clausesContainer = container.querySelector('.filterBarClausesContainer');
  if (!clausesContainer) {
    return { content: [{ type: "text", text: 'Filter clauses container not found' }] };
  }

  const results = [];

  for (let i = 0; i < filters.length; i++) {
    const { field, operator, value } = filters[i];

    // Step 2: Ensure row i exists — use first existing row for i=0, add new rows for i>0
    let rows = clausesContainer.querySelectorAll('.filterBarRowDiv');
    if (i >= rows.length) {
      // Click the "+" add button to create a new row
      const addIcon = container.querySelector('[class*="anticon-plus"]');
      if (addIcon) {
        const addBtn = addIcon.closest('button');
        if (addBtn) addBtn.click();
        await waitForRender(500);
      }
      rows = clausesContainer.querySelectorAll('.filterBarRowDiv');
      if (i >= rows.length) {
        results.push(`Row ${i + 1}: Could not add filter clause for "${field}"`);
        continue;
      }
    }

    const row = rows[i];
    let fieldSet = false, operatorSet = false, valueSet = false;

    // Step 3: Select the field (1st ant-select)
    if (field) {
      const selects = row.querySelectorAll('.ant-select');
      if (selects.length > 0) {
        fieldSet = await triggerAntdSelectChange(selects[0], field);
        await waitForRender(600);
      }
    }

    // Step 4: Select the operator (2nd ant-select, re-query after field re-render)
    if (operator) {
      const freshRow = clausesContainer.querySelectorAll('.filterBarRowDiv')[i];
      const selects = freshRow.querySelectorAll('.ant-select');
      if (selects.length >= 2) {
        operatorSet = await triggerAntdSelectChange(selects[1], operator);
        await waitForRender(400);
      }
    }

    // Step 5: Fill the value (re-query after operator re-render)
    if (value) {
      const freshRow = clausesContainer.querySelectorAll('.filterBarRowDiv')[i];
      const cols = freshRow.querySelectorAll('.ant-col');
      if (cols.length >= 4) {
        const valueCol = cols[3];

        // Wait for value input to become enabled (React enables it after field+operator are set)
        const valueInput = valueCol.querySelector('input');
        if (valueInput && valueInput.disabled) {
          await new Promise(resolve => {
            const check = () => {
              if (!valueInput.disabled) return resolve();
              requestAnimationFrame(check);
            };
            setTimeout(() => resolve(), 3000);
            check();
          });
        }

        const select = valueCol.querySelector('.ant-select');
        const picker = valueCol.querySelector('.ant-picker');
        const numberInput = valueCol.querySelector('.ant-input-number-input');
        const textInput = valueCol.querySelector('input');

        if (select) {
          valueSet = await triggerAntdSelectChange(select, value);
        } else if (picker) {
          const pickerInput = picker.querySelector('input');
          if (pickerInput) {
            setReactInputValue(pickerInput, value);
            pickerInput.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
            valueSet = true;
          }
        } else if (numberInput) {
          setReactInputValue(numberInput, value);
          numberInput.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
          valueSet = true;
        } else if (textInput) {
          setReactInputValue(textInput, value);
          valueSet = true;
        }
      }
      await waitForRender(300);
    }

    results.push(`${field || '(existing)'} ${operator || 'Like'} ${value || '(existing)'}${valueSet ? '' : ' [value may not have been set]'}`);
  }

  // Step 6: Click Apply ONCE after all filters are set (button is a sibling of filterBarContainer)
  const applyBtn = container.parentElement.querySelector('button.filterBarButton');
  if (applyBtn && !applyBtn.disabled) {
    applyBtn.click();
    await waitForRender(2000);
  }

  // Step 7: Trigger Search to execute the filtered query. Call the component's
  // getData() via the React fiber — synthetic clicks do not fire antd onSearch.
  const main = document.querySelector('main');
  await triggerAntdSearchAndLoad(main, 10000);

  return {
    content: [{ type: "text", text: `Applied ${filters.length} filter(s): ${results.join('; ')}` }]
  };
}


/**
 * Navigate pagination.
 */
async function paginate({ page_number, direction }) {
  if (page_number) {
    const pageItems = document.querySelectorAll('.ant-pagination-item');
    for (const item of pageItems) {
      const link = item.querySelector('a');
      if (link && link.textContent?.trim() === String(page_number)) {
        link.click();
        await waitForRender(2000);
        return { content: [{ type: "text", text: `Navigated to page ${page_number}` }] };
      }
    }
    return { content: [{ type: "text", text: `Page ${page_number} not found in pagination` }] };
  }

  if (direction === 'next') {
    const nextItem = document.querySelector('.ant-pagination-next');
    if (nextItem && !nextItem.classList.contains('ant-pagination-disabled')) {
      const link = nextItem.querySelector('a');
      if (link) {
        link.click();
        await waitForRender(2000);
        return { content: [{ type: "text", text: 'Navigated to next page' }] };
      }
    }
    return { content: [{ type: "text", text: 'Next page not available (already on last page or no pagination)' }] };
  } else if (direction === 'previous') {
    const prevItem = document.querySelector('.ant-pagination-prev');
    if (prevItem && !prevItem.classList.contains('ant-pagination-disabled')) {
      const link = prevItem.querySelector('a');
      if (link) {
        link.click();
        await waitForRender(2000);
        return { content: [{ type: "text", text: 'Navigated to previous page' }] };
      }
    }
    return { content: [{ type: "text", text: 'Previous page not available (already on first page or no pagination)' }] };
  }

  return { content: [{ type: "text", text: `Pagination action not found: ${direction || page_number}` }] };
}


/**
 * Search for bookings — navigates to search page, enters search term, clicks Search.
 */
async function searchBooking(params) {
  const searchTerm = params.search_term || params.so_number;

  // Navigate to search booking page if not already there
  if (!window.location.pathname.includes('/search_booking')) {
    const nav = safePush('/BMS/enhance_booking/search_booking');
    if (!nav.confirmed) return _navCanceledResult(nav.message);
    await waitForRender(2000);
  }

  // Enter search term into the booking page search bar (inside <main>, NOT the sidebar search)
  if (searchTerm) {
    const main = document.querySelector('main');
    const searchInput = main && (
      main.querySelector('input.ant-input[placeholder="Search"]')
      || main.querySelector('.ant-input-wrapper input.ant-input[placeholder="Search"]')
      || main.querySelector('.ant-select-selection-search-input')
    );
    if (searchInput) {
      searchInput.focus();
      setReactInputValue(searchInput, searchTerm);
      await waitForRender(500);
    } else {
      return { content: [{ type: "text", text: 'Search bar not found on this page.' }] };
    }
  }

  // Trigger the search and wait for results to load. A synthetic click on the
  // antd search button does NOT reliably fire onSearch -> getData, so we call
  // the list component's getData() directly via the React fiber (see
  // triggerAntdSearchAndLoad). Reading results any sooner raced the async load.
  const main = document.querySelector('main');
  await triggerAntdSearchAndLoad(main, 10000);

  // Read the results — count total and collect visible SO links
  const totalText = document.querySelector('.ant-pagination-total-text, [class*="total"]')?.textContent?.trim();
  const rows = document.querySelectorAll('a[href*="shipping_order"]');
  const results = Array.from(rows).slice(0, 10).map(a => ({
    so_number: a.textContent?.trim(),
    url: a.href
  }));

  const summary = totalText || (results.length > 0 ? `${results.length} results` : 'No results');
  return {
    content: [{ type: "text", text: results.length > 0
      ? `Search for "${searchTerm || 'all'}": ${summary}\n${results.map(r => r.so_number).join(', ')}`
      : `Search for "${searchTerm || 'all'}": No results found.` }]
  };
}


// ==================== New Tool Functions ====================

/**
 * Read form field VALUES (by control type) under `root` as "Label: value (type)" lines.
 * Unlike element.innerText, this captures <input>/<textarea> .value, which innerText
 * omits — so text fields (e.g. "Address in HBL") are read correctly instead of empty.
 */
/**
 * Read ONE form field's value given its detected type. Single source of truth for the
 * value-reading switch (used by readFormFieldsText; readForm delegates to it) so any
 * future selector fix is a one-line change. Covers all BMS control families incl.
 * multi-select/AutoComplete, Cascader/TreeSelect, RangePicker, Checkbox.Group, Upload,
 * Segmented/Rate/Slider/Transfer, plus a final fallback that captures display-only span
 * / read-only FormItem text (which reading only <input>.value would otherwise miss).
 */
function readFieldValue(formItem, type) {
  let value = '';
  if (type === 'select') {
    // multi-select / mode=tags -> join all tags; AutoComplete -> its search input value.
    const items = formItem.querySelectorAll('.ant-select-selection-item');
    if (items.length) {
      value = Array.from(items).map(e => e.textContent.trim()).join(', ');
    } else {
      const si = formItem.querySelector('.ant-select-selection-search-input');
      value = si ? si.value.trim() : '';
    }
  } else if (type === 'rangepicker') {
    value = Array.from(formItem.querySelectorAll('.ant-picker input')).map(i => i.value).filter(Boolean).join(' - ');
  } else if (type === 'datepicker') {
    const i = formItem.querySelector('.ant-picker input');
    value = i ? i.value : '';
  } else if (type === 'cascader' || type === 'treeselect') {
    value = Array.from(formItem.querySelectorAll('.ant-select-selection-item')).map(e => e.textContent.trim()).join(', ');
  } else if (type === 'segmented') {
    value = ((formItem.querySelector('.ant-segmented-item-selected') || {}).textContent || '').trim();
  } else if (type === 'rate') {
    value = String(formItem.querySelectorAll('.ant-rate-star-full').length + 0.5 * formItem.querySelectorAll('.ant-rate-star-half').length);
  } else if (type === 'slider') {
    const handle = formItem.querySelector('.ant-slider-handle');
    const track = formItem.querySelector('.ant-slider');
    value = (handle && handle.getAttribute('aria-valuenow')) || (track && track.getAttribute('aria-valuenow')) || '';
  } else if (type === 'upload') {
    value = Array.from(formItem.querySelectorAll('.ant-upload-list-item-name')).map(e => e.textContent.trim()).join(', ');
  } else if (type === 'transfer') {
    value = Array.from(formItem.querySelectorAll('.ant-transfer-list:last-child .ant-transfer-list-content-item:not(.ant-transfer-list-content-item-disabled)')).map(e => e.textContent.trim()).join(', ');
  } else if (type === 'checkboxgroup') {
    value = Array.from(formItem.querySelectorAll('.ant-checkbox-wrapper-checked')).map(e => e.textContent.trim()).join(', ');
  } else if (type === 'checkbox') {
    value = formItem.querySelector('.ant-checkbox-checked') ? 'checked' : 'unchecked';
  } else if (type === 'switch') {
    value = formItem.querySelector('.ant-switch-checked') ? 'on' : 'off';
  } else if (type === 'radio') {
    const r = formItem.querySelector('.ant-radio-button-wrapper-checked, .ant-radio-wrapper-checked');
    value = r ? r.textContent.trim() : '';
  } else if (type === 'inputnumber') {
    const i = formItem.querySelector('.ant-input-number input');
    value = i ? i.value : '';
  } else if (type === 'textarea') {
    const t = formItem.querySelector('textarea');
    value = t ? t.value : '';
  } else if (type === 'input') {
    const i = formItem.querySelector('input');
    value = i ? i.value : '';
  } else {
    // unknown / display-only span / read-only FormItem: capture whatever text the
    // control renders (e.g. region/country shown as a sibling <span>).
    const ctrl = formItem.querySelector('.ant-form-item-control');
    value = (ctrl ? (ctrl.innerText || '') : '').trim();
  }
  return value;
}

function readFormFieldsText(root) {
  const rootEl = root || document;
  const formItems = rootEl.querySelectorAll('.ant-form-item');
  const fields = [];

  for (const formItem of formItems) {
    const labelEl = formItem.querySelector('.ant-form-item-label label');
    if (!labelEl) continue;
    const label = labelEl.textContent.trim();
    if (!label || label === ':' || label === '*') continue;

    // ancestor-aware hidden check: getComputedStyle doesn't propagate ancestor
    // display:none, so use offsetParent / checkVisibility to also skip fields hidden by
    // an ancestor (e.g. an inactive antd Tab pane kept in the DOM).
    const hidden = formItem.checkVisibility ? !formItem.checkVisibility() : (formItem.offsetParent === null);
    if (hidden) continue;

    const type = detectFieldType(formItem);
    const isDisabled = !!formItem.querySelector('[disabled], .ant-input-disabled, .ant-select-disabled, .ant-radio-button-wrapper-disabled, .ant-switch-disabled');

    const value = readFieldValue(formItem, type);

    fields.push(`${label}: "${value}" (${type}${isDisabled ? ', disabled' : ''})`);
  }

  return fields.join('\n');
}

/**
 * Wait for form values in `panel` to STABILIZE. Party/agent Selects render their raw
 * VALUE (a party ID) first then async-resolve to the label, and text-input values
 * populate after the labels. Poll until both the selection-item texts and the input
 * values stop changing for two consecutive polls, so a subsequent read captures
 * resolved labels and populated values instead of transient IDs / empties.
 */
async function waitForFormStable(panel, maxWait = 8000) {
  if (!panel) return;
  const startTime = Date.now();
  let prevSig = null;
  let stableRounds = 0;

  while (Date.now() - startTime < maxWait) {
    const selectItems = panel.querySelectorAll('.ant-select-selection-item');
    const inputs = panel.querySelectorAll('.ant-form-item input');
    const hasValues = Array.from(selectItems).some(el => (el.textContent || '').trim().length > 0)
                   || Array.from(inputs).some(el => (el.value || '').trim().length > 0);
    if (hasValues) {
      const sig = Array.from(selectItems).map(el => (el.textContent || '').trim()).join('')
                + '' + Array.from(inputs).map(el => (el.value || '').trim()).join('');
      if (sig === prevSig) {
        stableRounds += 1;
        if (stableRounds >= 2) break;  // unchanged across 2 polls -> settled
      } else {
        stableRounds = 0;
      }
      prevSig = sig;
    }
    await waitForRender(300);
  }
}


/**
 * Read all form field values from the current page.
 */
async function readForm() {
  // Delegate to the shared reader (same field-value logic as readFormFieldsText) so the
  // value switch lives in exactly one place (readFieldValue) — no duplicate to drift.
  const text = readFormFieldsText(document);
  return { content: [{ type: "text", text: text || "No form fields found on this page." }] };
}


/**
 * Fill multiple text/number/textarea form fields at once.
 */
async function fillForm({ fields }) {
  const results = [];

  for (const field of fields) {
    let found = [];
    for (let attempt = 0; attempt < 5; attempt++) {
      found = findFormFieldsByLabel(field.label);
      if (found.length === 0 && field.name) found = findFormFieldsByName(field.name);
      if (found.length > 0) break;
      await waitForRender(600);
    }

    if (found.length === 0) {
      results.push(`${field.label || field.name}: not found`);
      continue;
    }

    const { control, type, formItem } = found[0];

    // Check disabled
    const isDisabled = !!formItem.querySelector('[disabled], .ant-input-disabled, .ant-select-disabled');
    if (isDisabled) {
      results.push(`${field.label}: skipped (disabled)`);
      continue;
    }

    // Handle by type
    if (type === 'input' || type === 'textarea') {
      setReactInputValue(control, field.value);
      results.push(`${field.label}: filled`);
    } else if (type === 'inputnumber') {
      const innerInput = formItem.querySelector('.ant-input-number input') || control.querySelector('input');
      if (innerInput) {
        setReactInputValue(innerInput, field.value);
        results.push(`${field.label}: filled`);
      } else {
        results.push(`${field.label}: could not find inner input`);
      }
    } else if (type === 'select') {
      results.push(`${field.label}: skipped (use select_option instead)`);
    } else if (type === 'datepicker') {
      results.push(`${field.label}: skipped (use set_date instead)`);
    } else if (type === 'checkbox' || type === 'switch') {
      results.push(`${field.label}: skipped (use toggle_checkbox instead)`);
    } else if (type === 'radio') {
      results.push(`${field.label}: skipped (use select_radio instead)`);
    } else {
      results.push(`${field.label}: unsupported type ${type}`);
    }

    await waitForRender(300);
  }

  return {
    content: [{ type: "text", text: results.join('\n') }]
  };
}


/**
 * Select an option in an antd Select dropdown.
 */
async function selectOption({ label, name, value }) {
  // Retry field lookup — form fields may still be rendering after tab switch.
  let found = [];
  for (let attempt = 0; attempt < 5; attempt++) {
    found = findFormFieldsByLabel(label || name);
    if (found.length === 0 && name) found = findFormFieldsByName(name);
    if (found.length > 0) break;
    await waitForRender(600);
  }

  if (found.length === 0) {
    return { content: [{ type: "text", text: `Select not found: ${label || name}` }] };
  }

  const { control, formItem } = found[0];

  // Check disabled
  if (formItem.querySelector('.ant-select-disabled')) {
    return { content: [{ type: "text", text: `Select is disabled: ${label || name}` }] };
  }

  const selectRoot = formItem.querySelector('.ant-select');
  const success = await triggerAntdSelectChange(selectRoot, value, label || name);

  return {
    content: [{ type: "text", text: success
      ? `Selected "${value}" in ${label || name}`
      : `Could not find option "${value}" in ${label || name}` }]
  };
}


/**
 * Set a date value in an antd DatePicker.
 */
async function setDate({ label, name, value }) {
  let found = [];
  for (let attempt = 0; attempt < 5; attempt++) {
    found = findFormFieldsByLabel(label || name);
    if (found.length === 0 && name) found = findFormFieldsByName(name);
    if (found.length > 0) break;
    await waitForRender(600);
  }

  if (found.length === 0) {
    return { content: [{ type: "text", text: `DatePicker not found: ${label || name}` }] };
  }

  const { formItem } = found[0];

  if (formItem.querySelector('.ant-picker-disabled')) {
    return { content: [{ type: "text", text: `DatePicker is disabled: ${label || name}` }] };
  }

  const input = formItem.querySelector('.ant-picker input');
  if (!input) {
    return { content: [{ type: "text", text: `DatePicker input not found: ${label || name}` }] };
  }

  // Convert YYYY-MM-DD to DD/MM/YYYY (BMS display format) and Date object
  const parts = value.split('-');
  if (parts.length !== 3) {
    return { content: [{ type: "text", text: `Invalid date format, expected YYYY-MM-DD: ${value}` }] };
  }
  const formatted = `${parts[2]}/${parts[1]}/${parts[0]}`;
  const newDate = moment(value, 'YYYY-MM-DD');

  // Use React fiber to call antd's onChange directly.
  // Direct DOM manipulation doesn't work because antd DatePicker controls
  // the input value via React state (the input has readonly attribute).
  const picker = formItem.querySelector('.ant-picker');
  const fiberKey = Object.keys(picker).find(k => k.startsWith('__reactFiber$') || k.startsWith('__reactInternalInstance$'));
  if (!fiberKey) {
    return { content: [{ type: "text", text: `Could not access React internals for: ${label || name}` }] };
  }

  let current = picker[fiberKey];
  let onChange = null;
  let depth = 0;
  while (current && depth < 15) {
    if (current.memoizedProps && current.memoizedProps.onChange) {
      onChange = current.memoizedProps.onChange;
      break;
    }
    current = current.return;
    depth++;
  }

  if (!onChange) {
    return { content: [{ type: "text", text: `Could not find onChange handler for: ${label || name}` }] };
  }

  // antd DatePicker onChange signature: onChange(date: Date, dateString: string)
  onChange(newDate, formatted);
  await waitForRender(300);

  // Close picker popup if it opened
  if (picker.classList.contains('ant-picker-focused')) {
    document.body.click();
    await waitForRender(200);
  }

  return {
    content: [{ type: "text", text: `Set ${label || name} to ${value}` }]
  };
}


/**
 * Check or uncheck a Checkbox or Switch.
 */
async function toggleCheckbox({ label, name, checked }) {
  // Try checkbox by wrapper text (e.g., "S/O Reference Not Applicable")
  const checkboxWrappers = document.querySelectorAll('.ant-checkbox-wrapper');
  for (const wrapper of checkboxWrappers) {
    const text = wrapper.textContent.trim();
    if (text.toLowerCase().includes((label || '').toLowerCase())) {
      const cb = wrapper.querySelector('.ant-checkbox');
      if (!cb) continue;
      const isChecked = cb.classList.contains('ant-checkbox-checked');
      if (isChecked !== checked) {
        wrapper.click();
        await waitForRender(300);
      }
      return {
        content: [{ type: "text", text: `${label}: ${checked ? 'checked' : 'unchecked'}` }]
      };
    }
  }

  // Try switch by FormItem label (with retry for slow renders)
  let found = [];
  for (let attempt = 0; attempt < 5; attempt++) {
    found = findFormFieldsByLabel(label || name);
    if (found.length === 0 && name) found = findFormFieldsByName(name);
    if (found.length > 0) break;
    await waitForRender(600);
  }

  // Fallback: search switches directly (handles custom layouts without .ant-form-item)
  if (found.length === 0) {
    const searchTerm = (label || name || '').toLowerCase();
    const mainContent = document.querySelector('.ant-tabs-tabpane-active') || document.querySelector('main') || document.body;
    const allSwitches = mainContent.querySelectorAll('.ant-switch');
    for (const sw of allSwitches) {
      // Check if the switch's row or nearby text contains the label
      const row = sw.closest('.ant-row');
      if (row && row.textContent.toLowerCase().includes(searchTerm)) {
        const isOn = sw.classList.contains('ant-switch-checked');
        if (isOn !== checked) {
          sw.click();
          await waitForRender(300);
        }
        return {
          content: [{ type: "text", text: `${label}: ${checked ? 'on' : 'off'}` }]
        };
      }
    }
    // Last resort: if NO label/name was given and exactly one switch is visible,
    // use it. Never grab an arbitrary switch when the user named a field that did
    // not match - that would silently toggle the wrong control.
    if (allSwitches.length === 1 && !(label || name)) {
      const sw = allSwitches[0];
      const isOn = sw.classList.contains('ant-switch-checked');
      if (isOn !== checked) {
        sw.click();
        await waitForRender(300);
      }
      return {
        content: [{ type: "text", text: `${label}: ${checked ? 'on' : 'off'}` }]
      };
    }
  }

  if (found.length === 0) {
    return { content: [{ type: "text", text: `Checkbox/Switch not found: ${label || name}` }] };
  }

  const { control, formItem } = found[0];

  // Handle switch
  if (control && control.classList.contains('ant-switch')) {
    const isOn = control.classList.contains('ant-switch-checked');
    if (isOn !== checked) {
      control.click();
      await waitForRender(300);
    }
    return {
      content: [{ type: "text", text: `${label}: ${checked ? 'on' : 'off'}` }]
    };
  }

  // Handle checkbox inside FormItem
  const cbWrapper = formItem.querySelector('.ant-checkbox-wrapper');
  if (cbWrapper) {
    const cb = cbWrapper.querySelector('.ant-checkbox');
    const isChecked = cb && cb.classList.contains('ant-checkbox-checked');
    if (isChecked !== checked) {
      cbWrapper.click();
      await waitForRender(300);
    }
    return {
      content: [{ type: "text", text: `${label}: ${checked ? 'checked' : 'unchecked'}` }]
    };
  }

  return { content: [{ type: "text", text: `No toggleable control found for: ${label || name}` }] };
}


/**
 * Select a radio button in a RadioGroup.
 */
async function selectRadio({ label, name, value }) {
  let found = [];
  // Retry up to 5x/600ms like selectOption/setDate/toggleCheckbox: after a tab
  // switch the radio group often has not mounted yet, and a single lookup falsely
  // reports 'Radio group not found' for a field that renders ~1s later.
  for (let attempt = 0; attempt < 5; attempt++) {
    found = findFormFieldsByLabel(label || name);
    if (found.length === 0 && name) {
      found = findFormFieldsByName(name);
    }
    if (found.length > 0) break;
    await waitForRender(600);
  }

  if (found.length === 0) {
    return { content: [{ type: "text", text: `Radio group not found: ${label || name}` }] };
  }

  const { formItem } = found[0];
  const radioGroup = formItem.querySelector('.ant-radio-group');
  if (!radioGroup) {
    return { content: [{ type: "text", text: `No radio group in: ${label || name}` }] };
  }

  // Find matching radio button by text
  const normalizedValue = value.toLowerCase().trim();
  const wrappers = radioGroup.querySelectorAll('.ant-radio-button-wrapper, .ant-radio-wrapper');

  for (const wrapper of wrappers) {
    const text = wrapper.textContent.trim().toLowerCase();
    if (text === normalizedValue || text.includes(normalizedValue)) {
      // Check if already selected
      if (wrapper.classList.contains('ant-radio-button-wrapper-checked') ||
          wrapper.classList.contains('ant-radio-wrapper-checked')) {
        return { content: [{ type: "text", text: `${label}: already set to ${value}` }] };
      }
      wrapper.click();
      await waitForRender(300);
      return { content: [{ type: "text", text: `${label}: selected ${value}` }] };
    }
  }

  // Also try matching by input value attribute
  const radioInputs = radioGroup.querySelectorAll('input[type="radio"]');
  for (const radio of radioInputs) {
    if (radio.value.toLowerCase() === normalizedValue) {
      radio.click();
      await waitForRender(300);
      return { content: [{ type: "text", text: `${label}: selected ${value}` }] };
    }
  }

  const available = Array.from(wrappers).map(w => w.textContent.trim()).join(', ');
  return { content: [{ type: "text", text: `Radio option "${value}" not found. Available: ${available}` }] };
}


/**
 * Handle any popup dialog — Modal (confirm/warning/info/success/error), Popconfirm, or Notification.
 * Clicks the appropriate button based on action or button_text.
 */
async function handlePopupConfirm({ action, button_text }) {
  // --- 1. Try Ant Design Modal dialogs first (.ant-modal-wrap) ---
  //    Visibility is driven by the inner .ant-modal (isElementVisible) so modals
  //    in custom roots or mid-transition are still handled. (M1)
  const modalWraps = document.querySelectorAll('.ant-modal-wrap');
  for (const wrap of modalWraps) {
    const modal = wrap.querySelector('.ant-modal');
    if (!modal || !isElementVisible(modal)) continue;

    const footer = modalFooterContainer(modal);
    if (!footer) {
      // No footer — try the X close button
      const closeBtn = modal.querySelector('.ant-modal-close');
      if (closeBtn) {
        closeBtn.click();
        await waitForRender(500);
        return { content: [{ type: "text", text: 'Closed modal (no footer)' }] };
      }
      continue;
    }

    const title = (modal.querySelector('.ant-modal-title')?.textContent || '').trim();
    const footerButtons = footer.querySelectorAll('button');

    // If button_text is specified, find the matching button
    if (button_text) {
      const targetLower = button_text.toLowerCase().trim();
      for (const btn of footerButtons) {
        const btnText = (btn.textContent || '').trim().toLowerCase();
        if (btnText === targetLower || btnText.includes(targetLower)) {
          btn.click();
          await waitForRender(500);
          return { content: [{ type: "text", text: `Clicked "${btn.textContent.trim()}" on modal "${title}"` }] };
        }
      }
      // Also try close (X) button if button_text suggests closing
      if (targetLower === 'close' || targetLower === 'x') {
        const closeBtn = modal.querySelector('.ant-modal-close');
        if (closeBtn) {
          closeBtn.click();
          await waitForRender(500);
          return { content: [{ type: "text", text: `Closed modal "${title}" via X button` }] };
        }
      }
    }

    // Standard OK/Cancel action
    if (action === 'ok' || action === 'confirm' || action === 'yes') {
      const okBtn = footer.querySelector('.ant-btn-primary');
      if (okBtn) {
        okBtn.click();
        await waitForRender(500);
        return { content: [{ type: "text", text: `Confirmed modal "${title}"` }] };
      }
      // Fallback: click last button (some modals only have one)
      const lastBtn = footerButtons[footerButtons.length - 1];
      if (lastBtn) {
        lastBtn.click();
        await waitForRender(500);
        return { content: [{ type: "text", text: `Clicked "${lastBtn.textContent.trim()}" on modal "${title}"` }] };
      }
    } else {
      // Cancel / dismiss
      const cancelBtn = footer.querySelector('.ant-btn-default');
      if (cancelBtn) {
        cancelBtn.click();
        await waitForRender(500);
        return { content: [{ type: "text", text: `Dismissed modal "${title}"` }] };
      }
      // Fallback: X button
      const closeBtn = modal.querySelector('.ant-modal-close');
      if (closeBtn) {
        closeBtn.click();
        await waitForRender(500);
        return { content: [{ type: "text", text: `Closed modal "${title}" via X button` }] };
      }
    }
  }

  // --- 2. Try Popconfirm (original behavior) ---
  const popovers = document.querySelectorAll('.ant-popover:not(.ant-popover-hidden)');
  for (const popover of popovers) {
    const popconfirm = popover.querySelector('.ant-popconfirm');
    if (!popconfirm) continue;

    const buttonsContainer = popconfirm.querySelector('.ant-popconfirm-buttons');
    if (!buttonsContainer) continue;

    // If button_text specified, try to match
    if (button_text) {
      const targetLower = button_text.toLowerCase().trim();
      const allBtns = buttonsContainer.querySelectorAll('button');
      for (const btn of allBtns) {
        const btnText = (btn.textContent || '').trim().toLowerCase();
        if (btnText === targetLower || btnText.includes(targetLower)) {
          btn.click();
          await waitForRender(500);
          return { content: [{ type: "text", text: `Clicked "${btn.textContent.trim()}" on popconfirm` }] };
        }
      }
    }

    if (action === 'ok' || action === 'confirm' || action === 'yes') {
      const okBtn = buttonsContainer.querySelector('.ant-btn-primary');
      if (okBtn) {
        okBtn.click();
        await waitForRender(500);
        return { content: [{ type: "text", text: 'Confirmed popconfirm' }] };
      }
    } else {
      const cancelBtn = buttonsContainer.querySelector('.ant-btn-default');
      if (cancelBtn) {
        cancelBtn.click();
        await waitForRender(500);
        return { content: [{ type: "text", text: 'Cancelled popconfirm' }] };
      }
    }
  }

  // --- 3. Try notification/alert close ---
  const notifClose = document.querySelector('.ant-notification-notice-close');
  if (notifClose) {
    notifClose.click();
    await waitForRender(300);
    return { content: [{ type: "text", text: 'Closed notification' }] };
  }

  const alertClose = document.querySelector('.ant-alert-close-icon, .ant-alert-close-text');
  if (alertClose) {
    alertClose.click();
    await waitForRender(300);
    return { content: [{ type: "text", text: 'Closed alert banner' }] };
  }

  return { content: [{ type: "text", text: 'No popup dialog found' }] };
}


/**
 * Dismiss ALL visible popups on the page.
 * For modals: clicks Cancel/X. For popconfirms: clicks Cancel.
 * For notifications/alerts: clicks close button.
 */
async function dismissAllPopups() {
  const results = [];
  const popups = detectPopups();

  if (popups.length === 0) {
    return { content: [{ type: "text", text: 'No popups found on the page' }] };
  }

  // 1. Dismiss Modal dialogs — click Cancel or X
  //    Visibility driven by the inner .ant-modal (isElementVisible). (M1)
  const modalWraps = document.querySelectorAll('.ant-modal-wrap');
  for (const wrap of modalWraps) {
    const modal = wrap.querySelector('.ant-modal');
    if (!modal || !isElementVisible(modal)) continue;

    const title = (modal.querySelector('.ant-modal-title, [class*="confirm-title"]')?.textContent || '').trim();

    // Try Cancel button first
    const cancelBtn = modalFooterContainer(modal)?.querySelector('.ant-btn-default');
    if (cancelBtn) {
      cancelBtn.click();
      results.push(`Closed modal "${title}" (Cancel)`);
      continue;
    }

    // Try X close button
    const closeBtn = modal.querySelector('.ant-modal-close');
    if (closeBtn) {
      closeBtn.click();
      results.push(`Closed modal "${title}" (X)`);
      continue;
    }

    // Last resort: click OK to dismiss
    const okBtn = modalFooterContainer(modal)?.querySelector('.ant-btn-primary');
    if (okBtn) {
      okBtn.click();
      results.push(`Closed modal "${title}" (OK — no cancel available)`);
    }
  }

  // 2. Dismiss Popconfirms — click Cancel
  const popovers = document.querySelectorAll('.ant-popover:not(.ant-popover-hidden)');
  for (const popover of popovers) {
    const popconfirm = popover.querySelector('.ant-popconfirm');
    if (!popconfirm) continue;
    const cancelBtn = popconfirm.querySelector('.ant-popconfirm-buttons .ant-btn-default');
    if (cancelBtn) {
      cancelBtn.click();
      results.push('Dismissed popconfirm');
    }
  }

  // 3. Dismiss notifications and alerts
  document.querySelectorAll('.ant-notification-notice-close').forEach(btn => {
    try { btn.click(); results.push('Closed notification'); } catch (e) { /* ignore */ }
  });
  document.querySelectorAll('.ant-alert-close-icon, .ant-alert-close-text').forEach(btn => {
    try { btn.click(); results.push('Closed alert banner'); } catch (e) { /* ignore */ }
  });

  await waitForRender(500);

  return {
    content: [{
      type: "text",
      text: results.length > 0
        ? `Dismissed ${results.length} popup(s): ${results.join('; ')}`
        : 'No dismissible popups found'
    }]
  };
}


/**
 * Fill a value in an inline-editable table cell.
 */
async function fillTableCell({ row_identifier, column_name, value }) {
  const table = document.querySelector('.ant-table') || document.querySelector('table');
  if (!table) {
    return { content: [{ type: "text", text: 'No table found on this page' }] };
  }

  // Map column headers to indices — exact match first, then fuzzy fallback
  const headers = Array.from(table.querySelectorAll('th, .ant-table-thead th'));
  let colIndex = -1;
  const colLower = column_name.toLowerCase().trim();

  // Exact match
  for (let i = 0; i < headers.length; i++) {
    const headerText = headers[i].textContent.trim().toLowerCase();
    if (headerText === colLower) {
      colIndex = i;
      break;
    }
  }
  // Fuzzy: match on alphanumerics only (so "PO" does NOT match "PORT"/"POD") and
  // require the token to be at least 3 chars to avoid ambiguous short prefixes.
  const colNorm = colLower.replace(/[^A-Za-z0-9]/g, '');
  if (colIndex === -1 && colNorm.length > 0) {
    for (let i = 0; i < headers.length; i++) {
      const headerNorm = headers[i].textContent.trim().toLowerCase().replace(/[^A-Za-z0-9]/g, '');
      if (headerNorm === colNorm) {
        colIndex = i;
        break;
      }
    }
  }

  if (colIndex === -1) {
    const headerNames = headers.map(h => h.textContent.trim()).join(', ');
    return { content: [{ type: "text", text: `Column "${column_name}" not found. Available: ${headerNames}` }] };
  }

  // Find target row
  const rows = table.querySelectorAll('tbody tr, .ant-table-tbody tr');
  // Prefer an EXACT cell-text match (e.g. the row item number "1") so a short
  // identifier never matches a container number or amount that merely contains
  // the digit. Fall back to substring only for longer (>=3 char) identifiers.
  const rowIdLower = String(row_identifier || '').toLowerCase().trim();
  const rowIdNorm = rowIdLower.replace(/[^A-Za-z0-9]/g, '');
  const shortId = rowIdLower.length < 3;
  for (const row of rows) {
    const cells = row.querySelectorAll('td');
    const cellTexts = Array.from(cells).map(c => c.textContent.trim().toLowerCase());
    const exactHit = cellTexts.indexOf(rowIdLower) >= 0;
    const normHit = rowIdNorm.length > 0 && cellTexts.some(ct => ct.replace(/[^A-Za-z0-9]/g, '') === rowIdNorm);
    const fuzzyHit = !shortId && cellTexts.some(ct => ct.includes(rowIdLower));
    if (exactHit || normHit || fuzzyHit) {
      const targetCell = cells[colIndex];
      if (!targetCell) {
        return { content: [{ type: "text", text: `Cell not found at column ${column_name} for row ${row_identifier}` }] };
      }

      // Check if cell is non-editable (prop-driven editable=false renders .tableEditDisplay)
      if (targetCell.querySelector('.tableEditDisplay')) {
        return { content: [{ type: "text", text: `Cell is not editable (table not in edit mode): ${column_name} / ${row_identifier}` }] };
      }

      // Detect cell type and fill — order matters: check specialized inputs first
      const numberInput = targetCell.querySelector('.ant-input-number-input');
      const select = targetCell.querySelector('.ant-select');
      const textarea = targetCell.querySelector('textarea');
      const input = targetCell.querySelector('input:not(.ant-input-number-input)');

      if (numberInput) {
        setReactInputValue(numberInput, value);
        await waitForRender(300);
      } else if (select) {
        await triggerAntdSelectChange(select, value);
      } else if (textarea) {
        setReactInputValue(textarea, value);
        await waitForRender(300);
      } else if (input) {
        setReactInputValue(input, value);
        await waitForRender(300);
      } else {
        return { content: [{ type: "text", text: `Cell has no editable control: ${column_name} / ${row_identifier}` }] };
      }

      return { content: [{ type: "text", text: `Filled ${column_name}="${value}" for row ${row_identifier}` }] };
    }
  }

  return { content: [{ type: "text", text: `Row "${row_identifier}" not found in table` }] };
}


// ==================== Batch / Combined Tools ====================

/**
 * Execute multiple form actions in a single call.
 * Avoids multiple LLM round-trips by batching all operations client-side.
 */
async function batchFormActions({ actions }) {
  // Reorder: move agent-related select_option actions to the end.
  // Selecting Consignee triggers a React re-render that clears agent fields,
  // so agents must be filled AFTER all other fields (especially Consignee).
  const agentKeywords = ['nomination agent', 'release agent'];
  const isAgentAction = (a) =>
    a.action === 'select_option' &&
    agentKeywords.some(kw => (a.label || '').toLowerCase().includes(kw));

  const reordered = [
    ...actions.filter(a => !isAgentAction(a)),
    ...actions.filter(a => isAgentAction(a)),
  ];

  const results = [];

  for (let i = 0; i < reordered.length; i++) {
    const { action: toolName, ...params } = reordered[i];
    console.log(`[BMS Tools] Batch ${i + 1}/${reordered.length}: ${toolName}`, params);

    try {
      const result = await executeTool(toolName, params);

      let text;
      if (result.success && result.result?.content?.[0]?.text) {
        text = result.result.content[0].text;
      } else if (result.error) {
        text = `Error: ${result.error}`;
      } else {
        text = 'Done';
      }

      results.push(`${i + 1}. ${toolName}: ${text}`);
    } catch (err) {
      results.push(`${i + 1}. ${toolName}: Error - ${err.message}`);
    }
  }

  return {
    content: [{ type: "text", text: results.join('\n') }]
  };
}


/**
 * Open a shipping order, click Edit, switch tab, and optionally read form.
 * Combines 3-4 separate tool calls into one client-side execution.
 */
async function openSoForEdit({ so_number, tab_name, read_form_after }) {
  const steps = [];

  // Step 1: Navigate to SO
  const navResult = await executeTool('open_shipping_order', { so_number });
  const navText = (navResult && navResult.success && navResult.result?.content?.[0]?.text) ? navResult.result.content[0].text : '';
  if (!navResult || !navResult.success || navResult.error || /^ERROR:/i.test(navText)) {
    const detail = (navResult && navResult.error) ? (': ' + navResult.error) : (navText ? (': ' + navText) : '');
    return { content: [{ type: 'text', text: `open_so_for_edit failed: could not open SO ${so_number}${detail}` }] };
  }
  steps.push(`Opened SO ${so_number}`);

  // Step 2: Click Edit
  await executeTool('click_button', { button_name: 'Edit' });
  steps.push('Clicked Edit');

  // Step 3: Switch tab if specified
  if (tab_name) {
    await executeTool('switch_so_tab', { tab_name });
    steps.push(`Switched to tab: ${tab_name}`);
  }

  // Step 4: Read form if requested
  let formContent = '';
  if (read_form_after) {
    const formResult = await executeTool('read_form', {});
    if (formResult.success && formResult.result?.content?.[0]?.text) {
      formContent = formResult.result.content[0].text;
    }
    steps.push('Read form values');
  }

  const combinedText = steps.join('\n');
  const fullText = formContent
    ? `${combinedText}\n\nCurrent form values:\n${formContent}`
    : combinedText;

  return {
    content: [{ type: "text", text: fullText }]
  };
}


/**
 * Navigate to a list/dashboard page, (optionally filter/search), click Search, and read it.
 * Combines navigate_to_page + apply_filters + click_button("Search") + read_current_page
 * into one client-side execution (one LLM round-trip).
 */
async function showList({ page, filters, search_term }) {
  const steps = [];

  if (search_term) {
    await executeTool('search_booking', { search_term });
    steps.push(`Searched bookings for "${search_term}"`);
  } else {
    await executeTool('navigate_to_page', { page });
    steps.push(`Navigated to ${page}`);
  }

  if (Array.isArray(filters) && filters.length) {
    await executeTool('apply_filters', { filters });
    steps.push(`Applied ${filters.length} filter(s)`);
  }

  // Click Search to load results (skip if search_booking already searched). Best-effort.
  if (!search_term) {
    try {
      const r = await executeTool('click_button', { button_name: 'Search' });
      steps.push(r && r.success ? 'Clicked Search' : 'No Search button (page may auto-load)');
    } catch (e) {
      steps.push('No Search button (page may auto-load)');
    }
  }

  let pageContent = '';
  const rd = await executeTool('read_current_page', {});
  if (rd && rd.success && rd.result && rd.result.content && rd.result.content[0]) {
    pageContent = rd.result.content[0].text || '';
  }

  const text = pageContent
    ? `Loaded ${page || 'list'}. Page content:\n${pageContent}`
    : `Loaded ${page || 'list'} but could not read content. Steps: ${steps.join('; ')}.`;
  return { content: [{ type: 'text', text }] };
}


/**
 * Open a shipping order and read a tab (or the header) — non-mutating (no Edit click).
 * Combines open_shipping_order + read_so_tab (or read_so_header) into one call.
 */
async function readSo({ so_number, tab_name }) {
  await executeTool('open_shipping_order', { so_number });

  const readTool = tab_name
    ? await executeTool('read_so_tab', { tab_name })
    : await executeTool('read_so_header', {});

  let content = '';
  if (readTool && readTool.success && readTool.result && readTool.result.content && readTool.result.content[0]) {
    content = readTool.result.content[0].text || '';
  }

  const label = tab_name ? `tab "${tab_name}"` : 'header';
  const text = content
    ? `Opened SO ${so_number}, read ${label}:\n${content}`
    : `Opened SO ${so_number} but could not read ${label}.`;
  return { content: [{ type: 'text', text }] };
}


// ==================== Tool Dispatcher ====================

/**
 * Force a fresh scan for blocking popups, giving a late async confirm modal
 * time to mount first. Escape hatch for the case where a confirm dialog
 * (e.g. PRS-overwrite) appeared AFTER the tool-result snapshot was sent and the
 * turn already ended (route_after_tool ends the turn once a result is returned).
 * The LLM calls this as the first action of the NEXT turn when a mutating action
 * showed no success signal and no popup. (M1 recovery path.)
 */
async function pollPopups() {
  await waitForPopup(4000);
  const popups = detectPopups();
  const info = formatBlockingPopups(popups);
  return { content: [{ type: "text", text: info ? info.replace(/^\n\n/, '') : 'No blocking popups detected' }] };
}

/**
 * Select the Nth proposed sailing option on the Booking Approval tab.
 *
 * "Approved Sailing" is NOT a dropdown — it's a read-only field that gets set by
 * CLICKING one of the proposed-sailing cards (.scheduleItem div inside the Nth
 * li.ant-list-item of .sailingScheduleResultList; onClick -> selectSailingSchedule).
 * select_option/click_button/click_list_item can't reach it, so the chatbot uses
 * this dedicated tool after setting Approval Status = "Approved".
 */
async function selectProposedSailing({ option_number }) {
  const n = parseInt(option_number, 10);
  if (!n || n < 1) {
    return { content: [{ type: "text", text: `Invalid option_number "${option_number}". Use a 1-based index (1, 2, ...).` }] };
  }

  const list = document.querySelector('.sailingScheduleResultList');
  if (!list) {
    return { content: [{ type: "text", text: 'No proposed sailing list (.sailingScheduleResultList) found. Make sure you are on the Booking Approval tab AND Approval Status is set to "Approved" — the sailing options only render then.' }] };
  }

  // The clickable card is .scheduleItem.expanded (onClick -> selectSailingSchedule).
  // It renders inside .sailingScheduleResultList as a div.ant-list-item (NOT <li>),
  // so target the card class directly.
  const cards = Array.from(list.querySelectorAll('.scheduleItem.expanded'));
  if (cards.length < n) {
    return { content: [{ type: "text", text: `Only ${cards.length} proposed sailing option(s) found; cannot pick option ${n}. Read the tab to see how many options are available.` }] };
  }

  const target = cards[n - 1];
  target.click();
  await realWait(1500);

  // Verify via the "Approved Sailing" FormItem control (shows "Option N" once set).
  const items = Array.from(document.querySelectorAll('.ant-form-item'));
  const f = items.find(function (it) { const l = it.querySelector('.ant-form-item-label'); return l && /Approved Sailing/.test(l.textContent); });
  const now = f ? ((f.querySelector('.ant-form-item-control') || {}).innerText || '') : '';
  const matched = new RegExp('Option ' + n + '(\\D|$)').test(now || '');
  return {
    content: [{
      type: "text",
      text: matched
        ? `Selected proposed sailing option ${n}. Approved Sailing now shows "${now.trim()}". Next: click_button("Save") to finalize, then handle any confirm popup (handle_popup_confirm).`
        : `Clicked proposed sailing option ${n}. Approved Sailing now reads "${(now || '(not visible yet)').trim()}". If it did not update, the option may be Unavailable; try the other option, then click_button("Save").`
    }]
  };
}


/**
 * Click a Save-style button AND confirm the popup it triggers, in one call.
 * Collapses click_button(Save) + handle_popup_confirm(ok) — the tail of every
 * BMS mutation — into a single client-side execution (one LLM round-trip).
 * Optionally reads the page back after the confirm so the caller can verify the
 * new state in the SAME turn. Never throws: returns an error string naming the
 * failed step.
 */
async function saveAndConfirm({ button_name, expect_popup, verify_read }) {
  const btn = button_name || 'Save';
  const steps = [];

  // Step 1: click the Save button.
  let clickText = '';
  try {
    const r = await executeTool('click_button', { button_name: btn });
    if (r && r.error) {
      return { content: [{ type: 'text', text: `save_and_confirm failed at click_button(${btn}): ${r.error}` }] };
    }
    clickText = (r && r.success && r.result?.content?.[0]?.text) ? r.result.content[0].text : '';
    steps.push(`Clicked ${btn}${clickText ? ': ' + clickText : ''}`);
  } catch (err) {
    return { content: [{ type: 'text', text: `save_and_confirm failed at click_button(${btn}): ${err.message}` }] };
  }

  // Step 2: if a confirm popup is expected, wait for it (up to ~4s) then confirm.
  let popupText = '';
  if (expect_popup) {
    try {
      const found = await waitForPopup(4000);
      if (found) {
        const r = await executeTool('handle_popup_confirm', { action: 'ok' });
        if (r && r.error) {
          return { content: [{ type: 'text', text: `save_and_confirm failed at handle_popup_confirm: ${r.error}` }] };
        }
        popupText = (r && r.success && r.result?.content?.[0]?.text) ? r.result.content[0].text : '';
      } else {
        popupText = 'SAVE UNCERTAIN: No confirm popup appeared within 4s (the Save click may not have committed - verify the record)';
      }
      steps.push(popupText || 'Confirmed popup');
    } catch (err) {
      return { content: [{ type: 'text', text: `save_and_confirm failed at handle_popup_confirm: ${err.message}` }] };
    }
  }

  // Step 3: optionally read the page back so the caller can verify the result.
  let readText = '';
  if (verify_read) {
    try {
      const r = await executeTool('read_current_page', {});
      if (r && r.error) {
        return { content: [{ type: 'text', text: `save_and_confirm failed at read_current_page: ${r.error}` }] };
      }
      readText = (r && r.success && r.result?.content?.[0]?.text) ? r.result.content[0].text : '';
    } catch (err) {
      return { content: [{ type: 'text', text: `save_and_confirm failed at read_current_page: ${err.message}` }] };
    }
  }

  const combined = steps.join('\n');
  const text = readText ? `${combined}\n\nPage after save:\n${readText}` : combined;
  return { content: [{ type: 'text', text }] };
}


/**
 * Approve a shipping order end-to-end in ONE call: open the SO, load the
 * Booking Approval tab, set Approval Status = Approved, pick the Nth proposed
 * sailing, then Save + confirm the "Confirm Booking Approval" popup.
 * Collapses the 6-step approve flow into a single client-side execution.
 * Approval Status is set BEFORE the sailing pick (the proposed-sailing options
 * only render once status is "Approved"). Never throws: returns an error string
 * naming the failed step.
 */
async function approveBooking({ so_number, sailing_option }) {
  const opt = parseInt(sailing_option, 10) || 1;
  const steps = [];

  // Run one primitive; on failure return an error string naming the step.
  const run = async function (label, toolName, params) {
    try {
      const r = await executeTool(toolName, params);
      if (r && r.error) {
        return { ok: false, text: `approve_booking failed at ${label}: ${r.error}` };
      }
      const t = (r && r.success && r.result?.content?.[0]?.text) ? r.result.content[0].text : '';
      // Primitives return soft failures as normal success text wrapped by
      // executeTool as {success:true} with no r.error: open_shipping_order emits
      // "ERROR: Could not open..." on /AccessDeclined, read_so_tab emits
      // "ERROR: No Shipping Order is currently open", select_option emits
      // "Select not found" / "Could not find option", and select_proposed_sailing
      // emits "No proposed sailing list" / "Only N proposed". Detect these and halt
      // so a mutating Save never fires on bad state, matching the docstring contract
      // "Never throws: returns an error string naming the failed step".
      const failed = /^ERROR:/i.test(t)
        || /not found|Could not find|Could not access|No proposed sailing list|Only \d+ proposed/i.test(t);
      if (failed) {
        return { ok: false, text: `approve_booking failed at ${label}: ${t}` };
      }
      steps.push(`${label}${t ? ': ' + t : ''}`);
      return { ok: true };
    } catch (err) {
      return { ok: false, text: `approve_booking failed at ${label}: ${err.message}` };
    }
  };

  // 1. Open the shipping order.
  let f = await run(`open_shipping_order(${so_number})`, 'open_shipping_order', { so_number });
  if (!f.ok) return { content: [{ type: 'text', text: f.text }] };

  // 2. Load the Booking Approval tab (switches + reads; reveals proposed sailings).
  f = await run('read_so_tab(Booking Approval)', 'read_so_tab', { tab_name: 'Booking Approval' });
  if (!f.ok) return { content: [{ type: 'text', text: f.text }] };

  // 3. Set Approval Status = Approved (MUST precede the sailing pick).
  f = await run('select_option(Approval Status=Approved)', 'select_option', { label: 'Approval Status', value: 'Approved' });
  if (!f.ok) return { content: [{ type: 'text', text: f.text }] };

  // 4. Pick the Nth proposed sailing.
  f = await run(`select_proposed_sailing(option ${opt})`, 'select_proposed_sailing', { option_number: opt });
  if (!f.ok) return { content: [{ type: 'text', text: f.text }] };

  // 5. Save + confirm the "Confirm Booking Approval" popup (collapses the save tail).
  let saveText = '';
  try {
    const sr = await saveAndConfirm({ button_name: 'Save', expect_popup: true, verify_read: false });
    saveText = (sr && sr.content?.[0]?.text) ? sr.content[0].text : '';
    // The run() helper guards steps 1-4 but step 5 bypassed it, so a failed Save
    // (e.g. 'save_and_confirm failed at ...') was reported as 'Approved ...'. Halt
    // on a failed save just like run() does for the earlier steps.
    if (/save_and_confirm failed at/i.test(saveText)) {
      return { content: [{ type: 'text', text: `approve_booking failed at save_and_confirm: ${saveText}` }] };
    }
    steps.push(`save_and_confirm${saveText ? ': ' + saveText : ''}`);
  } catch (err) {
    return { content: [{ type: 'text', text: `approve_booking failed at save_and_confirm: ${err.message}` }] };
  }

  const text = `Approved ${so_number} sailing option ${opt}.\n${steps.join('\n')}`;
  return { content: [{ type: 'text', text }] };
}


const TOOLS = {
  navigate_to_page:   navigateToPage,
  read_current_page:  readCurrentPage,
  click_button:       clickButton,
  click_list_item:    clickListItem,
  switch_so_tab:      switchSoTab,
  read_so_tab:        readSoTab,
  read_so_header:     readSoHeader,
  open_shipping_order: openShippingOrder,
  apply_custom_filter: applyCustomFilter,
  apply_filters:        applyFilters,
  paginate:           paginate,
  search_booking:     searchBooking,
  read_form:          readForm,
  fill_form:          fillForm,
  select_option:      selectOption,
  set_date:           setDate,
  toggle_checkbox:    toggleCheckbox,
  select_radio:       selectRadio,
  handle_popup_confirm: handlePopupConfirm,
  dismiss_all_popups: dismissAllPopups,
  poll_popups:        pollPopups,
  fill_table_cell:    fillTableCell,
  batch_form_actions: batchFormActions,
  open_so_for_edit:   openSoForEdit,
  show_list:          showList,
  read_so:            readSo,
  select_proposed_sailing: selectProposedSailing,
  save_and_confirm:   saveAndConfirm,
  approve_booking:    approveBooking,
};

/**
 * Execute a tool by name. Returns { success, result, toolName } or { error }.
 * Same return format as dummy_website2's executeWebMCPTool() function.
 */
export async function executeTool(toolName, params) {
  try {
    console.log(`[BMS Tools] Executing ${toolName} with params:`, params);

    const toolFn = TOOLS[toolName];
    if (!toolFn) {
      return {
        error: `Tool '${toolName}' not found. Available: ${Object.keys(TOOLS).join(', ')}`
      };
    }

    const result = await toolFn(params);
    console.log(`[BMS Tools] ${toolName} completed:`, result);

    // Detect blocking popups and append to the result text so the LLM learns a
    // popup is blocking. Detect BEFORE auto-dismiss so the signal is not
    // destroyed first (M5: the old order hid success/error toasts). For
    // popup-prone tools, poll briefly — async server round-trips can mount a
    // confirm modal 1-3s after the click and a single snapshot misses it (M1).
    // Skip for the popup-handling tools themselves (they report their own result
    // and re-detecting the modal they just dismissed can loop on close animation).
    const isPopupTool = toolName === 'handle_popup_confirm' || toolName === 'dismiss_all_popups';
    if (!isPopupTool) {
      // Mutating actions can trigger an ASYNC server-side confirm modal that
      // mounts 1-3s AFTER the action — most notably the PRS-overwrite
      // Modal.confirm "Do you want to overwrite the following e-Booking info by
      // PRS info?" shown after a Shipper/Consignee change. A single snapshot
      // misses it and the modal is left orphaned (blocking the next turn). For
      // popup-prone tools AND party-field changes, wait up to ~4s for a blocking
      // popup so the agent can handle_popup_confirm it instead.
      const popupProneTools = ['batch_form_actions', 'fill_table_cell', 'navigate_to_page', 'open_so_for_edit', 'switch_so_tab'];
      const isVerifyClick = toolName === 'click_button'
        && params && ACTION_VERIFY_BUTTONS.test(String(params.button_name || '').trim());
      const isPartyChange = (toolName === 'select_option' || toolName === 'select_radio' || toolName === 'toggle_checkbox')
        && params && /shipper|consignee|notify|agent/i.test(String(params.label || params.name || ''));
      if (popupProneTools.includes(toolName) || isVerifyClick || isPartyChange) {
        await waitForPopup(4000);
      }
      let popupInfo = formatBlockingPopups(detectPopups());
      // Auto-handle the blocking confirm modals that follow a party change so
      // they aren't orphaned on screen (the agent often didn't click through).
      // Direct DOM clicks (not handlePopupConfirm) avoid visibility races when
      // the PRS modal and an auto-save recovery modal stack on top of each other.
      if (isPartyChange && popupInfo) {
        await realWait(400);
        const rules = [
          // PRS-overwrite: confirm -> apply the new party's PRS info (the change requested).
          { label: 'PRS-overwrite', re: /overwrite the following e-Booking info by PRS info/i, btn: /^Confirm$/i },
          // Auto-save recovery: No -> don't load a stale autosave over current state.
          { label: 'auto-save recovery', re: /auto save record is found/i, btn: /^No$/i },
        ];
        const did = [];
        for (const wrap of document.querySelectorAll('.ant-modal-wrap')) {
          const modal = wrap.querySelector('.ant-modal');
          if (!modal) continue;
          const body = modal.textContent || '';
          const footer = modalFooterContainer(modal);
          const btns = footer ? Array.from(footer.querySelectorAll('button')) : [];
          for (const r of rules) {
            if (r.re.test(body)) {
              const b = btns.find(function (x) { return r.btn.test((x.textContent || '').trim()); });
              if (b) { b.click(); did.push(r.label + ' -> "' + (b.textContent || '').trim() + '"'); }
              break;
            }
          }
        }
        if (did.length) {
          await realWait(700);
          popupInfo = formatBlockingPopups(detectPopups());
          if (result?.content?.[0]?.text) {
            result.content[0].text += `\n\n[AUTO] ${did.join('; ')}.`;
          }
        }
      }
      if (popupInfo && result?.content?.[0]?.text) {
        result.content[0].text += popupInfo;
      }
    }

    // After detection: silently dismiss non-critical popups (toasts/notifications).
    autoDismissNonCriticalPopups();

    return {
      success: true,
      result: result,
      toolName: toolName
    };
  } catch (error) {
    console.error(`[BMS Tools] Error executing ${toolName}:`, error);
    return { error: error.message };
  }
}

/**
 * Get list of available tool names (for debugging).
 */
export function getAvailableTools() {
  return Object.keys(TOOLS);
}
